/* ============================================================
   La figura: cachorro sentado de frente, carcasa de acrílico
   transparente con el pelo del perro dentro.
   Referencia: el llavero de @thepawmy — orejas de acrílico
   transparentes, el pelo solo rellena cabeza y cuerpo, y se ve
   con textura de rizo a través de la pared.
   Se dibuja por instancia (no <use>) para poder animar cada
   pieza desde CSS.
   ============================================================ */

let dogN = 0;

/* oreja izquierda; la derecha es su espejo, así siempre son simétricas */
const EAR_D = "M80 62C54 56 24 72 22 102c-1 16 4 28 12 34q7 6 14 0q7 6 14-3C60 116 58 82 80 62Z";
const MIRROR = "translate(220,0) scale(-1,1)";
const INSET_EAR = "translate(51,99) scale(.85) translate(-51,-99)";

const EARS = `
    <path d="${EAR_D}"/>
    <path d="${EAR_D}" transform="${MIRROR}"/>`;

/* cabeza, cuello, cuerpo y las dos patas delanteras en un solo trazo */
const CORE = `
    <path d="M110 48C138.7 48 162 71.3 162 100c0 18-10 33-24 42 10 8 14 24 14 44 0 12-2 22-6 28 0 14-8 23-19 23-11 0-17-8-17-20 0 12-6 20-17 20-11 0-19-9-19-23-4-6-6-16-6-28 0-20 4-36 14-44-14-9-24-24-24-42C58 71.3 81.3 48 110 48Z"/>`;

/* el pelo: mismo volumen, un poco más pequeño, orejas incluidas */
const FUR_CORE = `
    <path d="M110 51c27 0 49 22 49 49 0 17-9 31-22 40 9 8 12 25 12 41 0 11-2 20-5 26 0 13-7 21-17 21-10 0-15-8-15-19 0 11-5 19-15 19-10 0-17-8-17-21-3-6-5-15-5-26 0-16 3-33 12-41-13-9-22-23-22-40 0-27 22-49 49-49Z"/>
    <path d="${EAR_D}" transform="${INSET_EAR}"/>
    <path d="${EAR_D}" transform="${MIRROR} ${INSET_EAR}"/>`;

/* rizos: nube de bucles con jitter determinista, no un patrón repetido */
function curlSet(seed){
  let s = seed;
  const rnd = () => (s = (s * 1664525 + 1013904223) % 4294967296) / 4294967296;
  const out = [];
  const cloud = (cx, cy, rx, ry, n) => {
    for(let i = 0; i < n; i++){
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(rnd());
      const x = cx + Math.cos(a) * rx * d;
      const y = cy + Math.sin(a) * ry * d;
      const r  = 1.7 + rnd() * 2.9;
      const a0 = rnd() * Math.PI * 2;
      const sw = 3.6 + rnd() * 2.0;
      const a1 = a0 + sw;
      out.push(`M${(x + Math.cos(a0) * r).toFixed(1)} ${(y + Math.sin(a0) * r).toFixed(1)}`
        + `A${r.toFixed(1)} ${r.toFixed(1)} 0 ${sw > Math.PI ? 1 : 0} 1 `
        + `${(x + Math.cos(a1) * r).toFixed(1)} ${(y + Math.sin(a1) * r).toFixed(1)}`);
    }
  };
  cloud(110, 100, 50, 50, 230);   // cabeza
  cloud(110, 180, 39, 36, 165);   // cuerpo
  cloud(110, 143, 30, 22, 55);    // cuello
  cloud(93, 215, 15, 20, 45);     // pata izquierda
  cloud(127, 215, 15, 20, 45);    // pata derecha
  cloud(48, 100, 20, 34, 75);     // oreja izquierda
  cloud(172, 100, 20, 34, 75);    // oreja derecha
  return out.join("");
}
/* un solo trazo con ~690 subtrazos: mismo dibujo, 700x menos nodos */
const CURLS = curlSet(20260905);

const shape = (parts, attrs = "") => `<g ${attrs}>${parts}</g>`;

function dogSVG(animated){
  const id = "d" + (++dogN);
  const cls = "dog" + (animated ? " dog--anim" : "");

  return `
<svg class="${cls}" viewBox="0 0 220 258" role="img" aria-label="">
  <defs>
    <linearGradient id="gl-${id}" x1=".1" y1="0" x2=".9" y2="1">
      <stop offset="0"   stop-color="#fff" stop-opacity=".92"/>
      <stop offset=".28" stop-color="#fff" stop-opacity=".14"/>
      <stop offset=".62" stop-color="#fff" stop-opacity=".06"/>
      <stop offset="1"   stop-color="#fff" stop-opacity=".7"/>
    </linearGradient>

    <radialGradient id="fu-${id}" cx=".35" cy=".2" r="1">
      <stop offset="0"   stop-color="var(--fur)"/>
      <stop offset=".45" stop-color="var(--fur)"/>
      <stop offset="1"   stop-color="var(--fur-dark)"/>
    </radialGradient>

    <!-- el pelo no tiene contorno limpio -->
    <filter id="sf-${id}" x="-25%" y="-25%" width="150%" height="150%">
      <feTurbulence type="fractalNoise" baseFrequency=".95" numOctaves="4" seed="7" result="n"/>
      <feDisplacementMap in="SourceGraphic" in2="n" scale="4" xChannelSelector="R" yChannelSelector="G"/>
    </filter>

    <radialGradient id="sh-${id}" cx=".5" cy=".52" r=".6">
      <stop offset=".5"  stop-color="var(--fur-dark)" stop-opacity="0"/>
      <stop offset="1"   stop-color="var(--fur-dark)" stop-opacity=".44"/>
    </radialGradient>

    <clipPath id="cp-${id}">${FUR_CORE}</clipPath>

    <!-- máscara de llenado: baja de la cabeza a las patas -->
    <mask id="mk-${id}">
      <rect class="dog__fillrect" x="-30" y="-30" width="280" height="320" fill="#fff"/>
    </mask>
  </defs>

  <!-- anilla -->
  <g class="dog__ring">
    <circle cx="110" cy="15" r="10" fill="none" stroke="#B5AA9E" stroke-width="4"/>
    <rect x="103" y="22" width="14" height="28" rx="6" fill="#CDC3B6"/>
  </g>

  <!-- pelo dentro (solo cabeza y cuerpo, como en el original) -->
  <g class="dog__fur" mask="url(#mk-${id})">
    ${shape(FUR_CORE, `fill="url(#fu-${id})" filter="url(#sf-${id})"`)}
    <g clip-path="url(#cp-${id})">
      <path d="${CURLS}" fill="none" stroke="var(--fur-dark)" stroke-opacity=".20" stroke-width="1.05"/>
      <path d="${CURLS}" fill="none" stroke="#fff" stroke-opacity=".11" stroke-width="1" transform="translate(-1.1,-1.1)"/>
      <rect x="0" y="0" width="220" height="258" fill="url(#sh-${id})"/>
    </g>
  </g>

  <!-- carcasa de acrílico -->
  <g class="dog__shell">
    ${shape(EARS + CORE, `fill="url(#gl-${id})"`)}
    ${shape(EARS + CORE, `fill="none" stroke="rgba(58,42,30,.24)" stroke-width="2.2"`)}
    ${shape(EARS, `fill="none" stroke="rgba(255,255,255,.6)" stroke-width="1.4" transform="translate(110,108) scale(.92) translate(-110,-108)"`)}
    ${shape(CORE, `fill="none" stroke="rgba(255,255,255,.5)" stroke-width="3" transform="translate(110,145) scale(.97) translate(-110,-145)"`)}
    <ellipse cx="84" cy="70" rx="19" ry="11" fill="#fff" opacity=".5" transform="rotate(-34 84 70)"/>
    <path d="M78 176c-4 18-1 34 9 45" fill="none" stroke="#fff" stroke-opacity=".38" stroke-width="6" stroke-linecap="round"/>
    <path d="M33 94c-3 12-2 23 3 32" fill="none" stroke="#fff" stroke-opacity=".5" stroke-width="5" stroke-linecap="round"/>
  </g>

  <!-- cara grabada en el acrílico -->
  <g class="dog__face" fill="rgba(44,31,22,.85)">
    <ellipse cx="90" cy="104" rx="8.2" ry="9"/>
    <ellipse cx="130" cy="104" rx="8.2" ry="9"/>
    <ellipse cx="87.2" cy="100.4" rx="2.7" ry="2.9" fill="#fff" opacity=".92"/>
    <ellipse cx="127.2" cy="100.4" rx="2.7" ry="2.9" fill="#fff" opacity=".92"/>
    <path d="M110 118c7.4 0 12 3.1 12 7.4s-5.3 8.1-12 8.1-12-3.8-12-8.1 4.6-7.4 12-7.4Z"/>
    <path d="M110 133.5c0 5.8-4.8 8.7-9.8 6.8M110 133.5c0 5.8 4.8 8.7 9.8 6.8"
          fill="none" stroke="rgba(44,31,22,.85)" stroke-width="2.6" stroke-linecap="round"/>
  </g>

  <!-- destello al cerrar -->
  <g class="dog__spark" fill="#fff">
    <path d="M58 64 l3.2 9 9 3.2 -9 3.2 -3.2 9 -3.2 -9 -9 -3.2 9 -3.2Z"/>
  </g>

  ${animated ? `
  <!-- pinzas de precisión -->
  <g class="dog__tweezers">
    <g class="dog__tuft">
      <ellipse cx="110" cy="44" rx="15" ry="10" fill="url(#fu-${id})" filter="url(#sf-${id})"/>
      <path d="M99 40c-6-3-9-8-8-13M121 41c6-4 8-9 7-14" fill="none"
            stroke="var(--fur-dark)" stroke-opacity=".65" stroke-width="2" stroke-linecap="round"/>
    </g>
    <path d="M74 -96 L101 36 L110 48 L95 34 L86 -96Z" fill="#32323A"/>
    <path d="M146 -96 L119 36 L110 48 L125 34 L134 -96Z" fill="#3E3E48"/>
    <path d="M77 -96 L86 -96 L97 22 L92 20Z" fill="#5A5A66" opacity=".75"/>
  </g>` : ``}
</svg>`;
}

function renderDogs(){
  document.querySelectorAll("[data-dog]").forEach(el => {
    if(!el.dataset.rendered){
      el.innerHTML = dogSVG(el.hasAttribute("data-dog-anim"));
      el.dataset.rendered = "1";
    }
  });
  document.querySelectorAll("[data-dog3d-fija]").forEach(el => {
    if(!el.dataset.rendered){
      el.innerHTML = `<div class="pieza dog" role="img" aria-label=""><canvas class="pieza__gl" aria-hidden="true"></canvas></div>`;
      el.dataset.rendered = "1";
      initFiguraFija(el);
    }
  });
  document.querySelectorAll("[data-dog3d-compra]").forEach(el => {
    if(!el.dataset.rendered){
      el.innerHTML = `<div class="pieza dog" role="img" aria-label=""><canvas class="pieza__gl" aria-hidden="true"></canvas><div class="pieza__sombra"></div></div>`;
      el.dataset.rendered = "1";
      initFiguraCompra(el);
    }
  });
  document.querySelectorAll("[data-dog3d]").forEach(el => {
    if(!el.dataset.rendered){
      el.innerHTML = dog3D();
      el.dataset.rendered = "1";
    }
  });
  document.querySelectorAll("[data-huella3d-compra]").forEach(el => {
    if(!el.dataset.rendered){
      el.innerHTML = `<div class="pieza pieza--huella"><canvas class="pieza__gl" aria-hidden="true"></canvas><div class="pieza__sombra"></div></div>`;
      el.dataset.rendered = "1";
      initHuellaCompra(el);
    }
  });
  document.querySelectorAll("[data-huella3d]").forEach(el => {
    if(!el.dataset.rendered){
      el.innerHTML = huella3D();
      el.dataset.rendered = "1";
    }
  });
  initEscena();
  initEscenaHuella();
  initRendersCarrito();
}


/* ============================================================
   La figura del hero en 3D real, controlada por el scroll.

   Es la pieza que se vende: caniche toy sentado, carcasa de
   acrílico gruesa, pelo rizado dentro, ojos y nariz de bola negra,
   anilla y cadena doradas. Se modela con funciones de distancia
   (elipsoides fundidos en una sola superficie) y se dibuja con un
   único sombreador WebGL, sin librerías:

     · acrílico: reflejo de Fresnel + refracción hacia el interior
     · pelo: volumen con rizos en relieve, relleno por un nivel
       horizontal real (la superficie del relleno se ve)
     · ojos, nariz, anilla y cadena: materiales propios

   Solo se dibuja cuando cambia el estado del scroll.
   ============================================================ */

const FIGURA_VS = "attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}";

/* el pelo: lo comparten la figura y el llavero de huella. Usa uRes, uFur y uFurDark */
const PELO_GLSL = `
float hash13(vec3 p){
  p = fract(p * 0.1031);
  p += dot(p, p.zyx + 31.32);
  return fract((p.x + p.y) * p.z);
}
float vnoise(vec3 p){
  vec3 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(hash13(i),                        hash13(i + vec3(1.0, 0.0, 0.0)), f.x),
                 mix(hash13(i + vec3(0.0, 1.0, 0.0)),  hash13(i + vec3(1.0, 1.0, 0.0)), f.x), f.y),
             mix(mix(hash13(i + vec3(0.0, 0.0, 1.0)),  hash13(i + vec3(1.0, 0.0, 1.0)), f.x),
                 mix(hash13(i + vec3(0.0, 1.0, 1.0)),  hash13(i + vec3(1.0, 1.0, 1.0)), f.x), f.y), f.z);
}
vec3 rumbo(vec3 p){
  vec3 w = vec3(vnoise(p * 2.2), vnoise(p * 2.2 + 17.3), vnoise(p * 2.2 + 41.9)) - 0.5;
  return normalize(vec3(p.x * 0.7, -1.0, p.z * 0.5) + w * 2.6);
}
vec3 hash33(vec3 p){
  p = fract(p * vec3(0.1031, 0.1030, 0.0973));
  p += dot(p, p.yxz + 33.33);
  return fract((p.xxy + p.yxx) * p.zyx);
}
/* Pelos sueltos: en cada celda de una rejilla 3D hay un pelo, un segmento
   corto con el rumbo del mechón y algo de desorden, tumbado sobre la
   superficie. Se mira en las 27 celdas vecinas cuánto se acerca cada pelo
   al punto contando solo la distancia sobre la superficie: así se ven
   también los que quedan más adentro, más tenues, y el pelo tiene fondo.
   El grosor nunca baja de ~1 px (la cámara abarca 3 unidades de alto). */
float pelos(vec3 p, vec3 n, vec3 dir, float cells){
  vec3 x = p * cells, i = floor(x);
  float ancho = max(0.07, 1.1 * cells / (uRes.y / 3.0));
  float acc = 0.0;
  for(int a = -1; a <= 1; a++)
  for(int b = -1; b <= 1; b++)
  for(int c = -1; c <= 1; c++){
    vec3 g = i + vec3(float(a), float(b), float(c));
    vec3 v = x - g - hash33(g);
    vec3 d = normalize(dir + (hash33(g + 19.19) - 0.5) * 1.1);
    d = normalize(d - n * dot(d, n) * 0.9);
    float t = clamp(dot(v, d), -1.0, 1.0);
    vec3 q = v - d * t;
    float prof = dot(q, n);
    float r = length(q - n * prof);
    acc += (1.0 - smoothstep(0.0, ancho, r))       // el pelo
         * (1.0 - smoothstep(0.45, 1.0, abs(t)))   // puntas finas
         * (0.25 + 0.75 * exp(-prof * prof * 3.0)); // los de dentro, tenues
  }
  return 1.0 - exp(-acc * 1.4);
}
/* dos capas: pelos largos y otros más cortos y finos que rellenan entre ellos;
   en móvil (LOW) una sola, algo más densa. Devuelve cuánto pelo hay en .x
   (los largos) y en .y (los finos) */
vec2 pelo(vec3 po, vec3 no, out float mech, out vec3 dir){
  mech = vnoise(po * 3.1) * 0.6 + vnoise(po * 6.7 + 5.0) * 0.4;
  dir = rumbo(po);
#ifdef LOW
  return vec2(pelos(po, no, dir, 23.0), 0.0);
#else
  return vec2(pelos(po, no, dir, 17.0),
              pelos(po + 7.3, no, normalize(dir + (rumbo(po * 2.0 + 3.0) - dir) * 0.5), 31.0));
#endif
}

/* tono: mechones algo más claros u oscuros a trozos; entre pelo y pelo, un
   poco de sombra; los largos cogen luz y los finos casi no */
vec3 peloCol(vec2 h, float mech){
  vec3 base = mix(uFurDark, uFur, clamp(0.3 + 0.6 * mech, 0.0, 1.0));
  base *= 0.9 + 0.1 * max(h.x, h.y);
  vec3 claro = min(uFur * 1.12 + 0.03, vec3(1.0));
  base = mix(base, mix(base, claro, 0.55), h.y * 0.7);
  return mix(base, claro, h.x * 0.55);
}
`;

const FIGURA_FS = `
precision highp float;
uniform vec2  uRes;
uniform mat3  uRot;     // objeto -> mundo
uniform mat3  uInv;     // mundo -> objeto
uniform float uLevel;   // altura del relleno (mundo)
uniform float uPhase;   // fase de la ondulación de la superficie
uniform float uFill;    // 0 = vacía: el rayo no busca pelo
uniform vec3  uFur;
uniform vec3  uFurDark;
uniform float uTw;      // 1 = pinzas en escena
uniform mat3  uTwInv;   // mundo -> pinzas (punta en el origen, mango hacia +y)
uniform vec3  uTwPos;   // punta de las pinzas (mundo)
uniform float uGap;     // separación de las puntas
uniform float uTuft;    // tamaño del mechón que llevan (0 = ya soltado)
uniform float uGlint;   // posición de la franja de luz que cruza el acrílico al cerrar
uniform float uGlintA;  // su intensidad (0 = no hay destello)
uniform float uOpen;    // 0 = tapada; > 0 = el tapón de la base está fuera
uniform mat3  uCapRot;  // giro inverso del tapón (objeto -> tapón)
uniform vec3  uCapOff;  // desplazamiento del tapón (objeto)
uniform float uRaza;    // molde: 0 caniche, 1 pomerania, 2 golden
uniform float uFondo;   // 0..1: fundido del borde de abajo (solo boca abajo, por la cadena)

const vec3 PIVOT = vec3(0.0, -0.05, 0.0);

float sdEllipsoid(vec3 p, vec3 r){
  float k0 = length(p / r);
  float k1 = length(p / (r * r));
  return k0 * (k0 - 1.0) / max(k1, 1e-4);
}
float smin(float a, float b, float k){
  float h = max(k - abs(a - b), 0.0) / k;
  return min(a, b) - h * h * k * 0.25;
}
vec3 rotZ(vec3 p, float a){
  float c = cos(a), s = sin(a);
  return vec3(c * p.x - s * p.y, s * p.x + c * p.y, p.z);
}
float sdTorus(vec3 p, float R, float r){
  vec2 q = vec2(length(p.xz) - R, p.y);
  return length(q) - r;
}
float sdLink(vec3 p, float le, float r1, float r2){
  vec3 q = vec3(p.x, max(abs(p.y) - le, 0.0), p.z);
  return length(vec2(length(q.xy) - r1, q.z)) - r2;
}

float sdRoundCone(vec3 p, vec3 a, vec3 b, float r1, float r2){
  vec3 ba = b - a;
  float l2 = dot(ba, ba), rr = r1 - r2, a2 = l2 - rr * rr, il2 = 1.0 / l2;
  vec3 pa = p - a;
  float y = dot(pa, ba), z = y - l2;
  vec3 xv = pa * l2 - ba * y;
  float x2 = dot(xv, xv), y2 = y * y * l2, z2 = z * z * l2;
  float k = sign(rr) * rr * rr * x2;
  if(sign(z) * a2 * z2 > k) return sqrt(x2 + z2) * il2 - r2;
  if(sign(y) * a2 * y2 < k) return sqrt(x2 + y2) * il2 - r1;
  return (sqrt(x2 * a2 * il2) + y * rr) * il2 - r1;
}

/* Volumen del pelo = hueco interior de la carcasa (espacio del objeto).
   Tres moldes, uno por raza que se vende (uRaza); el hero es el caniche.
   Pomerania y golden salen de img/razas.jpg: el pomerania es una masa de
   pelo con la cara de zorro delante; el golden, el de orejas largas. */
float bodyCaniche(vec3 p){
  vec3 q = vec3(abs(p.x), p.y, p.z);
  float d = sdEllipsoid(p - vec3(0.0, 0.57, 0.0), vec3(0.60, 0.57, 0.52));            // cabeza
  d = smin(d, sdEllipsoid(p - vec3(0.0, 0.29, 0.39), vec3(0.23, 0.17, 0.20)), 0.10);  // hocico
  d = smin(d, sdEllipsoid(rotZ(q - vec3(0.57, 0.31, -0.03), -0.26),
                          vec3(0.21, 0.34, 0.20)), 0.07);                            // orejas
  d = smin(d, sdEllipsoid(p - vec3(0.0, -0.48, -0.06), vec3(0.50, 0.47, 0.43)), 0.14); // cuerpo
  d = smin(d, sdEllipsoid(q - vec3(0.36, -0.84, -0.12), vec3(0.25, 0.21, 0.29)), 0.10); // ancas
  d = smin(d, sdEllipsoid(q - vec3(0.15, -0.82, 0.21), vec3(0.135, 0.30, 0.15)), 0.06); // patas
  return d;
}
/* Pomerania: escultura completa, con melena, patas y cola en todos los ángulos. */
float bodyPomerania(vec3 p){
  vec3 q = vec3(abs(p.x), p.y, p.z);
  // Melena redonda; relieve suave que continúa por los flancos y la nuca.
  vec3 mane = p - vec3(0.0, 0.40, -0.04);
  float angle = atan(mane.y, abs(mane.x) + 0.0001);
  float lob = pow(0.5 + 0.5 * cos(angle * 12.0), 2.0);
  float edge = smoothstep(0.32, 0.58, length(mane.xy));
  float d = sdEllipsoid(mane, vec3(0.65, 0.64, 0.50)) - 0.028 * lob * edge;
  // Torso más estrecho que la melena, sentado sobre ancas bien definidas.
  d = smin(d, sdEllipsoid(p - vec3(0.0, -0.48, -0.12), vec3(0.46, 0.51, 0.43)), 0.14);
  d = smin(d, sdEllipsoid(p - vec3(0.0, -0.12, 0.20), vec3(0.44, 0.38, 0.32)), 0.12);
  // Mejillas y hocico corto; se conservan los puntos de encaje de ojos y nariz.
  d = smin(d, sdEllipsoid(p - vec3(0.0, 0.46, 0.30), vec3(0.36, 0.30, 0.28)), 0.06);
  d = smin(d, sdRoundCone(p, vec3(0.0, 0.38, 0.46), vec3(0.0, 0.335, 0.6), 0.16, 0.1), 0.1);   // hocico corto y redondo
  float ear = sdRoundCone(q, vec3(0.32, 0.87, 0.0), vec3(0.41, 1.18, -0.025), 0.145, 0.025);
  ear = -smin(-ear, -(abs(q.z + 0.01) - 0.075), 0.05);
  d = smin(d, ear, 0.055);
  // Patas separadas, cortas y con una base estable.
  d = smin(d, sdRoundCone(q, vec3(0.21, -0.40, 0.29), vec3(0.21, -0.94, 0.36), 0.13, 0.105), 0.055);
  d = smin(d, sdEllipsoid(q - vec3(0.21, -1.0, 0.43), vec3(0.145, 0.085, 0.18)), 0.045);
  d = smin(d, sdEllipsoid(q - vec3(0.39, -0.76, -0.18), vec3(0.23, 0.28, 0.29)), 0.09);
  d = smin(d, sdEllipsoid(q - vec3(0.43, -1.0, 0.02), vec3(0.15, 0.085, 0.20)), 0.045);
  // Cola de pluma: nace en la grupa y se curva sobre un lado de la espalda.
  float tail = sdRoundCone(p, vec3(0.12, -0.62, -0.43), vec3(0.43, -0.26, -0.62), 0.15, 0.21);
  tail = smin(tail, sdRoundCone(p, vec3(0.43, -0.26, -0.62), vec3(0.47, 0.13, -0.60), 0.21, 0.23), 0.09);
  tail = smin(tail, sdRoundCone(p, vec3(0.47, 0.13, -0.60), vec3(0.20, 0.32, -0.53), 0.23, 0.15), 0.09);
  return smin(d, tail, 0.065);
}
float bodyGolden(vec3 p){
  vec3 q = vec3(abs(p.x), p.y, p.z);
  float d = sdEllipsoid(p - vec3(0.0, 0.86, -0.03), vec3(0.34, 0.3, 0.31));                 // cráneo ancho
  d = smin(d, sdEllipsoid(p - vec3(0.0, 0.68, 0.08), vec3(0.24, 0.18, 0.24)), 0.12);       // mejillas
  d = smin(d, sdEllipsoid(p - vec3(0.0, 0.67, 0.38), vec3(0.15, 0.12, 0.29)), 0.08);        // hocico largo y recto
  d = smin(d, sdEllipsoid(p - vec3(0.0, 0.6, 0.3), vec3(0.12, 0.07, 0.22)), 0.06);          // mandíbula
  /* orejas: triángulo plano pegado a la mejilla, ancho arriba donde se dobla
     y con la punta redondeada a la altura de la boca (dos elipsoides) */
  float oreja = smin(sdEllipsoid(q - vec3(0.345, 0.8, -0.02), vec3(0.08, 0.15, 0.16)),
                     sdEllipsoid(q - vec3(0.365, 0.57, 0.05), vec3(0.072, 0.15, 0.1)), 0.12);
  d = smin(d, oreja, 0.025);                                                               // poca fusión: el borde de la oreja se ve
  d = smin(d, sdEllipsoid(p - vec3(0.0, 0.42, -0.04), vec3(0.27, 0.3, 0.27)), 0.14);        // cuello
  d = smin(d, sdEllipsoid(p - vec3(0.0, 0.0, 0.06), vec3(0.38, 0.42, 0.3)), 0.16);          // pecho ancho
  d = smin(d, sdEllipsoid(p - vec3(0.0, -0.12, 0.22), vec3(0.24, 0.3, 0.18)), 0.1);         // pechera
  d = smin(d, sdEllipsoid(p - vec3(0.0, -0.45, -0.22), vec3(0.38, 0.5, 0.34)), 0.16);       // vientre, hacia atrás
  d = smin(d, sdRoundCone(q, vec3(0.18, -0.3, 0.2), vec3(0.18, -0.96, 0.24), 0.11, 0.095), 0.06); // patas: rectas hasta el suelo
  d = smin(d, sdEllipsoid(q - vec3(0.18, -1.01, 0.33), vec3(0.115, 0.07, 0.14)), 0.04);     // manos
  d = smin(d, sdEllipsoid(q - vec3(0.36, -0.72, -0.2), vec3(0.2, 0.3, 0.34)), 0.1);         // ancas
  d = smin(d, sdEllipsoid(q - vec3(0.44, -1.02, 0.06), vec3(0.11, 0.065, 0.16)), 0.05);     // pies de atrás, asomando
  return d;
}
float body(vec3 p){
  if(uRaza < 0.5) return bodyCaniche(p);
  if(uRaza < 1.5) return bodyPomerania(p);
  return bodyGolden(p);
}
/* carcasa: el mismo volumen engordado, más el ojal de la anilla */
float shell(vec3 p){
  float d = body(p) - 0.06;
  float ojal = sdTorus((p - vec3(0.0, 1.24, 0.0)).xzy, 0.085, 0.042);
  return smin(d, ojal, 0.04);
}
/* ojos y nariz: medio hundidos en la carcasa, como en el caniche (el centro
   ~0,025 por dentro de la superficie, medido evaluando esta misma SDF) */
float beads(vec3 p){
  vec3 q = vec3(abs(p.x), p.y, p.z);
  float e, n;
  if(uRaza < 0.5){
    e = length(q - vec3(0.215, 0.62, 0.52)) - 0.082;
    n = sdEllipsoid(p - vec3(0.0, 0.335, 0.63), vec3(0.083, 0.064, 0.06));
  }else if(uRaza < 1.5){
    e = length(q - vec3(0.17, 0.52, 0.579)) - 0.065;
    n = sdEllipsoid(p - vec3(0.0, 0.335, 0.74), vec3(0.062, 0.05, 0.048));
  }else{
    e = length(q - vec3(0.15, 0.88, 0.3)) - 0.062;
    n = sdEllipsoid(p - vec3(0.0, 0.7, 0.71), vec3(0.085, 0.064, 0.06));
  }
  return min(e, n);
}
float metal(vec3 p){
  float ring = sdTorus((p - vec3(0.0, 1.36, 0.0)).yxz, 0.12, 0.021);
  float l1 = sdLink((p - vec3(0.0, 1.57, 0.0)).zyx, 0.06, 0.055, 0.017);
  float l2 = sdLink( p - vec3(0.0, 1.76, 0.0),       0.06, 0.055, 0.017);
  float l3 = sdLink((p - vec3(0.0, 1.95, 0.0)).zyx, 0.06, 0.055, 0.017);
  return min(ring, min(l1, min(l2, l3)));
}


/* pinzas de acero: dos brazos planos unidos atrás, con el mechón entre las puntas */
float tweezers(vec3 pw, out float isTuft){
  vec3 p = uTwInv * (pw - uTwPos);
  isTuft = 0.0;
  float tuft = 9.0;
  if(uTuft > 0.01)
    tuft = sdEllipsoid(p - vec3(0.0, -0.06 * uTuft, 0.0), vec3(0.1, 0.13, 0.085) * uTuft);
  float bound = length(vec3(p.x, p.y - clamp(p.y, 0.0, 1.8), p.z)) - 0.2;
  if(bound > 0.1) return min(bound, tuft);
  vec3 q = vec3(abs(p.x), p.y, p.z / 2.6);
  float arm = sdRoundCone(q, vec3(uGap + 0.012, 0.0, 0.0), vec3(0.12, 0.80, 0.0), 0.013, 0.024);
  arm = min(arm, sdRoundCone(q, vec3(0.12, 0.80, 0.0), vec3(0.03, 1.78, 0.0), 0.024, 0.028));
  arm *= 0.7;
  if(tuft < arm){ isTuft = 1.0; return tuft; }
  return arm;
}

vec3 toObj(vec3 pw){ return uInv * (pw - PIVOT); }

/* El pelo entra por un agujero en la base, entre las ancas, y un tapón lo
   cierra. Destapada, la carcasa lleva ese agujero (solo atraviesa la pared
   de abajo); tapada es una sola pieza, sin juntas. */
const vec3 HOLE = vec3(0.0, -0.99, -0.1);         // centro del agujero, en la superficie
float holed(vec3 po){
  float cyl = length(po.xz - HOLE.xz) - 0.14;
  return max(shell(po), -max(cyl, po.y + 0.9));
}
/* el tapón: una arandela plana y el vástago que entra en el agujero */
float capSDF(vec3 po){
  vec3 q = uCapRot * (po - HOLE - uCapOff);
  float b = length(q) - 0.3;
  if(b > 0.1) return b;
  vec2 d1 = vec2(length(q.xz) - 0.165, abs(q.y + 0.018) - 0.018);
  float fl = min(max(d1.x, d1.y), 0.0) + length(max(d1, 0.0)) - 0.008;
  vec2 d2 = vec2(length(q.xz) - 0.122, abs(q.y - 0.045) - 0.05);
  float st = min(max(d2.x, d2.y), 0.0) + length(max(d2, 0.0)) - 0.008;
  return min(fl, st);
}

float mapOuter(vec3 pw, out float m){
  vec3 p = toObj(pw);
  float d = uOpen > 0.0 ? holed(p) : shell(p);
  float b = beads(p);
  float g = metal(p);
  m = 0.0;
  if(uOpen > 0.0){
    float c = capSDF(p);
    if(c < d){ d = c; m = 5.0; }                    // 5 = el tapón, fuera de su sitio
  }
  if(b < d){ d = b; m = 1.0; }
  if(g < d){ d = g; m = 2.0; }
  if(uTw > 0.5){
    float tf;
    float w = tweezers(pw, tf);
    if(w < d){ d = w; m = tf > 0.5 ? 4.0 : 3.0; }
  }
  return d;
}
/* la carcasa por la que va el rayo: con el agujero abierto o tapada */
float shellW(vec3 pw){ vec3 po = toObj(pw); return uOpen > 0.0 ? holed(po) : shell(po); }
float fur(vec3 pw){
  float wave = 0.012 * sin(pw.x * 9.0 + uPhase) * sin(pw.z * 8.0 - uPhase * 0.7);
  /* el pelo llega hasta 0,034 de la cara de fuera (la carcasa es body - 0,06):
     con más pared, orejas, patas y huecos estrechos se veían vacíos */
  return max(body(toObj(pw)) - 0.026, pw.y - (uLevel + wave));
}

vec3 nOuter(vec3 p){
  float m; vec2 e = vec2(0.0015, -0.0015);
  return normalize(e.xyy * mapOuter(p + e.xyy, m) + e.yyx * mapOuter(p + e.yyx, m) +
                   e.yxy * mapOuter(p + e.yxy, m) + e.xxx * mapOuter(p + e.xxx, m));
}
vec3 nShell(vec3 p){
  vec2 e = vec2(0.002, -0.002);
  return normalize(e.xyy * shellW(p + e.xyy) + e.yyx * shellW(p + e.yyx) +
                   e.yxy * shellW(p + e.yxy) + e.xxx * shellW(p + e.xxx));
}
vec3 nTw(vec3 p){
  float tf; vec2 e = vec2(0.0015, -0.0015);
  return normalize(e.xyy * tweezers(p + e.xyy, tf) + e.yyx * tweezers(p + e.yyx, tf) +
                   e.yxy * tweezers(p + e.yxy, tf) + e.xxx * tweezers(p + e.xxx, tf));
}
vec3 nFur(vec3 p){
  vec2 e = vec2(0.002, -0.002);
  return normalize(e.xyy * fur(p + e.xyy) + e.yyx * fur(p + e.yyx) +
                   e.yxy * fur(p + e.yxy) + e.xxx * fur(p + e.xxx));
}

/* estudio: caja de luz arriba a la izquierda, tira a la derecha, suelo cálido */
vec3 env(vec3 d){
  vec3 c = mix(vec3(0.50, 0.46, 0.42), vec3(1.0, 0.99, 0.97), smoothstep(-0.25, 0.85, d.y));
  c *= mix(0.62, 1.0, smoothstep(-0.8, 0.1, d.y));
  c += 2.0 * pow(max(dot(d, normalize(vec3(-0.55, 0.62, 0.56))), 0.0), 36.0);
  c += 0.9 * smoothstep(0.90, 0.985, dot(d, normalize(vec3(0.92, 0.12, 0.38))));
  return c;
}
/* lo que se ve a través del acrílico: la sala, un poco más oscura abajo */
vec3 envT(vec3 d){
  vec3 c = mix(vec3(0.42, 0.39, 0.36), vec3(0.97, 0.95, 0.92), smoothstep(-0.35, 0.65, d.y));
  c = mix(c, vec3(0.80, 0.77, 0.72), smoothstep(0.55, 0.95, abs(d.x)));   // paredes laterales
  c += 0.9 * pow(max(dot(d, normalize(vec3(-0.55, 0.62, 0.56))), 0.0), 24.0);
  return c;
}

/* ---------- el pelo ----------
   El pelo de verdad, apretado contra el acrílico, no es grano: son fibras
   largas que van en mechones, cada uno con su rumbo, con zonas algo más
   claras y más oscuras donde se amontona. Se construye así:
     · rumbo: cae hacia abajo y hacia fuera, torcido por un ruido suave
     · fibras: ruido estirado a lo largo de ese rumbo y fino a lo ancho;
       donde cruza la mitad se afila en una línea, que es un pelo
     · mechones: ruido grande que aclara y oscurece a trozos
   Las fibras más finas que ~2 px se apagan en vez de parpadear. */
${PELO_GLSL}

vec3 furShade(vec3 pw, vec3 rd){
  vec3 n = nFur(pw);
  vec3 po = toObj(pw);
  float mech; vec3 dir;
  vec2 h = pelo(po, uInv * n, mech, dir);
  vec3 L1 = normalize(vec3(-0.55, 0.65, 0.55));
  vec3 L2 = normalize(vec3(0.8, 0.2, 0.45));
  /* luz envolvente: el pelo deja pasar algo de luz, no tiene sombras duras */
  float d1 = clamp((dot(n, L1) + 0.45) / 1.45, 0.0, 1.0);
  float d2 = clamp((dot(n, L2) + 0.3) / 1.3, 0.0, 1.0);
  float ao = clamp(fur(pw + n * 0.09) / 0.09, 0.35, 1.0);
  vec3 col = peloCol(h, mech) * (0.52 + 0.48 * d1 + 0.14 * d2) * mix(1.0, ao, 0.6);
  /* brillo de fibra (Kajiya-Kay): una franja suave de través al pelo */
  vec3 T = normalize(uRot * dir);
  float th = dot(T, normalize(L1 - rd));
  float sheen = pow(max(1.0 - th * th, 0.0), 12.0);
  return col + min(uFur * 1.2, vec3(1.0)) * sheen * 0.12 * (0.35 + h.x);
}

vec3 tuftCol(vec3 p, vec3 n){
  vec3 L1 = normalize(vec3(-0.55, 0.65, 0.55));
  vec3 pt = uTwInv * (p - uTwPos);
  float mech; vec3 dir;
  vec2 h = pelo(pt + vec3(3.1), uTwInv * n, mech, dir);
  float dl = clamp((dot(n, L1) + 0.45) / 1.45, 0.0, 1.0);
  return peloCol(h, mech) * (0.52 + 0.52 * dl);
}
/* acero cepillado: tono propio con luz difusa + reflejo suave del estudio */
vec3 steelCol(vec3 n, vec3 rd){
  vec3 L1 = normalize(vec3(-0.55, 0.65, 0.55));
  float cosi = clamp(dot(-rd, n), 0.0, 1.0);
  vec3 refl = reflect(rd, n);
  float F = 0.35 + 0.65 * pow(1.0 - cosi, 5.0);
  float dl = clamp((dot(n, L1) + 0.4) / 1.4, 0.0, 1.0);
  vec3 col = vec3(0.60, 0.61, 0.64) * (0.45 + 0.55 * dl) * (1.0 - 0.4 * F) + env(refl) * vec3(0.85, 0.86, 0.9) * 0.4 * F;
  return col + vec3(1.0) * pow(max(dot(refl, L1), 0.0), 40.0) * 0.9;
}

/* al cerrar, una franja de luz cruza la carcasa en diagonal: dura un
   instante y solo existe sobre el acrílico, nunca como un brillo suelto */
float glint(vec3 pw){
  if(uGlintA <= 0.0) return 0.0;
  float s = dot(toObj(pw), vec3(0.62, 0.74, 0.26));
  float b = (s - uGlint) / 0.11;
  return exp(-b * b) * uGlintA;
}

/* el mango de las pinzas y la cadena se desvanecen hacia los bordes de arriba
   y de abajo (boca abajo, la cadena cuelga hacia abajo) */
vec4 arriba(vec4 c){
  float y = gl_FragCoord.y / uRes.y;
  /* abajo solo se funde boca abajo: derecha, ahí están las patas */
  return c * (1.0 - smoothstep(0.86, 0.995, y)) * mix(1.0, smoothstep(0.0, 0.035, y), uFondo);
}

void main(){
  vec2 uv = gl_FragCoord.xy / uRes * 2.0 - 1.0;
  float aspect = uRes.x / uRes.y;
  vec3 ro = vec3(0.0, 0.25, 7.0);
  float tanH = 1.5 / 7.0;
  vec3 rd = normalize(vec3(uv.x * tanH * aspect, uv.y * tanH, -1.0));

  /* esfera envolvente: fuera de ella no se marcha nada */
  vec3 oc = ro - vec3(0.0, 0.35, 0.0);
  float R0 = uOpen > 0.0 ? 2.3 : 1.9;
  float bb = dot(oc, rd), cc = dot(oc, oc) - R0 * R0, hh = bb * bb - cc;
  float t = 0.0, tEnd = 12.0;
  if(uTw < 0.5){                                  // sin pinzas basta la esfera
    if(hh < 0.0){ gl_FragColor = vec4(0.0); return; }
    hh = sqrt(hh);
    t = max(-bb - hh, 0.0); tEnd = -bb + hh;
  }

  float m = 0.0; bool hit = false;
  for(int i = 0; i < STEPS; i++){
    float d = mapOuter(ro + rd * t, m);
    if(d < 0.0015){ hit = true; break; }
    t += d * 0.9;
    if(t > tEnd) break;
  }
  if(!hit){ gl_FragColor = vec4(0.0); return; }

  vec3 p = ro + rd * t;
  vec3 n = nOuter(p);
  vec3 L1 = normalize(vec3(-0.55, 0.65, 0.55));
  vec3 L2 = normalize(vec3(0.8, 0.2, 0.45));
  float cosi = clamp(dot(-rd, n), 0.0, 1.0);
  vec3 refl = reflect(rd, n);

  if(m > 4.5){                                   // el tapón suelto: acrílico esmerilado
    float Fc = 0.06 + 0.94 * pow(1.0 - cosi, 5.0);
    float d1 = clamp((dot(n, L1) + 0.4) / 1.4, 0.0, 1.0);
    float d2 = clamp((dot(n, L2) + 0.2) / 1.2, 0.0, 1.0);
    vec3 cc = vec3(0.93, 0.915, 0.89) * (0.62 + 0.34 * d1 + 0.1 * d2) * (1.0 - Fc) + env(refl) * Fc;
    cc += vec3(1.0) * pow(max(dot(refl, L1), 0.0), 60.0) * 0.8;
    gl_FragColor = arriba(vec4(cc, 1.0)); return;
  }
  else if(m > 3.5){                              // mechón en las pinzas
    gl_FragColor = arriba(vec4(tuftCol(p, n), 1.0)); return;
  }
  else if(m > 2.5){                              // acero de las pinzas
    gl_FragColor = arriba(vec4(steelCol(n, rd), 1.0)); return;
  }
  else if(m > 1.5){                              // oro
    float F = 0.62 + 0.38 * pow(1.0 - cosi, 5.0);
    vec3 col = env(refl) * vec3(1.0, 0.76, 0.36) * F;
    col += vec3(1.0, 0.92, 0.75) * pow(max(dot(refl, L1), 0.0), 50.0);
    gl_FragColor = arriba(vec4(col, 1.0)); return;
  }
  else if(m > 0.5){                              // ojos y nariz
    float F = 0.05 + 0.95 * pow(1.0 - cosi, 5.0);
    vec3 col = vec3(0.022, 0.019, 0.017) + env(refl) * F * 0.8;
    col += vec3(1.0) * pow(max(dot(refl, L1), 0.0), 140.0) * 1.8;
    col += vec3(1.0) * pow(max(dot(refl, L2), 0.0), 80.0) * 0.35;
    gl_FragColor = arriba(vec4(col, 1.0)); return;
  }

  /* acrílico */
  float F = 0.04 + 0.96 * pow(1.0 - cosi, 5.0);
  vec3 spec = vec3(1.0) * (pow(max(dot(refl, L1), 0.0), 90.0) * 1.3 +
                           pow(max(dot(refl, L2), 0.0), 50.0) * 0.45);
  vec3 reflC = env(refl);

  vec3 ri = refract(rd, n, 1.0 / 1.49);
  if(dot(ri, ri) < 0.001) ri = rd;

  /* dentro: el pelo, la salida por el otro lado y, si están dentro, las
     puntas de las pinzas, que han entrado por el agujero de la base */
  float ti = 0.012, tf = 0.0; bool furHit = false, exitHit = false, twHit = false;
  vec3 q = p;
  for(int i = 0; i < ISTEPS; i++){
    q = p + ri * ti;
    float df = uFill > 0.0 ? fur(q) : 9.0;
    float ds = -shellW(q);
    float dt = uTw > 0.5 ? tweezers(q, tf) : 9.0;
    if(df < 0.002){ furHit = true; break; }
    if(dt < 0.002){ twHit = true; break; }
    if(ds < 0.002 && ti > 0.03){ exitHit = true; break; }
    ti += max(min(min(df, ds), dt) * 0.9, 0.004);
    if(ti > 3.0) break;
  }

  float gl = glint(p);
  if(twHit){
    vec3 nt = nTw(q);
    vec3 ct = (tf > 0.5 ? tuftCol(q, nt) : steelCol(nt, ri)) * (1.0 - F) * 0.95 + reflC * F + spec;
    gl_FragColor = arriba(vec4(ct, 1.0));
    return;
  }

  if(furHit){
    vec3 col = furShade(q, ri) * (1.0 - F) * 0.97 + reflC * F + spec;
    col += vec3(1.0, 0.99, 0.96) * gl * (0.28 + 0.5 * F);
    gl_FragColor = arriba(vec4(col, 1.0));
    return;
  }

  /* vacía: reflejo exterior + lo que se ve refractado a través de la pieza */
  float a = F;
  vec3 col = reflC * F;
  vec3 tdir = ri;
  float F2 = 0.0;
  if(exitHit){
    vec3 n2 = -nShell(q);                        // normal hacia dentro
    F2 = 0.04 + 0.96 * pow(1.0 - clamp(dot(-ri, n2), 0.0, 1.0), 5.0);
    vec3 r2 = refract(ri, n2, 1.49);
    tdir = dot(r2, r2) < 0.001 ? reflect(ri, n2) : r2;
    col += env(reflect(ri, n2)) * F2 * 0.5 * (1.0 - F);
    a += F2 * 0.5 * (1.0 - F);
  }
  float T = (1.0 - F) * (1.0 - F2) * (0.20 + clamp(ti * 0.08, 0.0, 0.14));
  col += envT(tdir) * T;
  a += T;
  col += spec + vec3(1.0, 0.99, 0.96) * gl * 0.4;
  a = clamp(a + max(max(spec.r, spec.g), spec.b) + gl * 0.4, 0.0, 1.0);
  gl_FragColor = arriba(vec4(min(col, vec3(a)), a));
}`;

/* base ortonormal de las pinzas a partir de su eje (de la punta al mango) */
function twBasis(y){
  let x0 = [y[1], -y[0], 0];                      // perpendicular, en el plano de la pantalla
  const lx = Math.hypot(...x0); x0 = x0.map(v => v / lx);
  const z0 = [x0[1] * y[2] - x0[2] * y[1], x0[2] * y[0] - x0[0] * y[2], x0[0] * y[1] - x0[1] * y[0]];
  /* giradas 55° sobre su eje: se ve la cara plana de los brazos y también la apertura */
  const c = Math.cos(.96), sn = Math.sin(.96);
  const x = x0.map((v, i) => c * v + sn * z0[i]);
  const z = z0.map((v, i) => -sn * x0[i] + c * v);
  /* matriz mundo -> pinzas: filas x, y, z; en columnas para WebGL */
  return new Float32Array([x[0], y[0], z[0], x[1], y[1], z[1], x[2], y[2], z[2]]);
}

/* Postura del tapón según lo fuera que esté (0 → 1), en el espacio de la
   figura: primero sale recto del agujero y luego se aparta a un lado,
   ladeándose. Al volver hace el mismo camino al revés. */
function capPose(o){
  const cl = v => Math.min(1, Math.max(0, v));
  const sm = t => t * t * (3 - 2 * t);
  const a = sm(cl(o / .45)), b = sm(cl((o - .3) / .7));
  return { off: [.9 * b, -.32 * a - .22 * b, .1 * b], rz: .8 * b, rx: -.3 * b };
}

/* ------------------------------------------------------------
   Resolución de los lienzos de los heros. Quieta, la pieza se dibuja
   nítida (hasta 2 px por px CSS en móvil). En movimiento, si el equipo no
   llega (fotogramas de más de 40 ms seguidos), baja un escalón, nunca por
   debajo de 1 px por px CSS; al pararse vuelve a dibujarse nítida. Un tope
   de 30 fps (modo de ahorro, navegadores dentro de WhatsApp o Instagram)
   no cuenta como lento: bajar la resolución no lo arregla y solo la
   pixelaba.
   ------------------------------------------------------------ */
function resolucionLienzo(low){
  const dpr = window.devicePixelRatio || 1;
  const nitida = Math.min(dpr, low ? 2 : 1.75);
  const suelo = Math.min(nitida, 1);
  const r = { nitida, mov: Math.min(nitida, 1.5), slow: 0 };
  r.medir = (raw) => {
    if(raw >= 100) return;
    r.slow = r.slow * .88 + (raw > 40 ? .12 : 0);
    if(r.slow > .55 && r.mov > suelo){ r.mov = Math.max(suelo, r.mov * .8); r.slow = 0; }
  };
  r.para = (moviendo) => moviendo ? r.mov : r.nitida;
  return r;
}

function crearFiguraGL(canvas, low){
  const gl = canvas.getContext("webgl", {
    alpha: true, premultipliedAlpha: true, antialias: false,
    depth: false, stencil: false, powerPreference: "high-performance"
  });
  if(!gl) return null;

  const sh = (type, src) => {
    const o = gl.createShader(type);
    gl.shaderSource(o, src);
    gl.compileShader(o);
    if(!gl.getShaderParameter(o, gl.COMPILE_STATUS)){
      console.warn("figura 3D:", gl.getShaderInfoLog(o));
      return null;
    }
    return o;
  };
  const defs = low ? "#define STEPS 56\n#define ISTEPS 30\n#define LOW 1\n"
                   : "#define STEPS 72\n#define ISTEPS 40\n";
  const vs = sh(gl.VERTEX_SHADER, FIGURA_VS);
  const fs = sh(gl.FRAGMENT_SHADER, defs + FIGURA_FS);
  if(!vs || !fs) return null;
  const prog = gl.createProgram();
  gl.attachShader(prog, vs); gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if(!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const aLoc = gl.getAttribLocation(prog, "a");
  gl.enableVertexAttribArray(aLoc);
  gl.vertexAttribPointer(aLoc, 2, gl.FLOAT, false, 0, 0);

  const u = {};
  ["uRes", "uRot", "uInv", "uLevel", "uPhase", "uFill", "uFur", "uFurDark",
   "uTw", "uTwInv", "uTwPos", "uGap", "uTuft", "uGlint", "uGlintA",
   "uOpen", "uCapRot", "uCapOff", "uRaza", "uFondo"]
    .forEach(n => u[n] = gl.getUniformLocation(prog, n));

  /* R = Rx · Ry · Rz, en orden de columnas para WebGL */
  function rot(ax, ay, az){
    const [cx, sx, cy, sy, cz, sz] = [Math.cos(ax), Math.sin(ax), Math.cos(ay), Math.sin(ay), Math.cos(az), Math.sin(az)];
    const ry = [[cy, 0, sy], [0, 1, 0], [-sy, 0, cy]];
    const rx = [[1, 0, 0], [0, cx, -sx], [0, sx, cx]];
    const rz = [[cz, -sz, 0], [sz, cz, 0], [0, 0, 1]];
    const mul = (A, B) => A.map((r, i) => [0, 1, 2].map(j => r[0] * B[0][j] + r[1] * B[1][j] + r[2] * B[2][j]));
    return mul(mul(rx, ry), rz);
  }
  const cols = M => new Float32Array([M[0][0], M[1][0], M[2][0], M[0][1], M[1][1], M[2][1], M[0][2], M[1][2], M[2][2]]);
  const rows = M => new Float32Array([M[0][0], M[0][1], M[0][2], M[1][0], M[1][1], M[1][2], M[2][0], M[2][1], M[2][2]]); // traspuesta

  return {
    gl,
    draw(s){
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(u.uRes, canvas.width, canvas.height);
      const R = rot(s.ax, s.ay, s.az);
      gl.uniformMatrix3fv(u.uRot, false, cols(R));
      gl.uniformMatrix3fv(u.uInv, false, rows(R));
      gl.uniform1f(u.uLevel, s.level);
      gl.uniform1f(u.uPhase, s.phase);
      gl.uniform1f(u.uFill, s.fill);
      gl.uniform3fv(u.uFur, s.fur);
      gl.uniform3fv(u.uFurDark, s.furDark);
      gl.uniform1f(u.uGlint, s.glint || 0);
      gl.uniform1f(u.uGlintA, s.glintA || 0);
      gl.uniform1f(u.uRaza, s.raza || 0);
      gl.uniform1f(u.uFondo, s.fondo || 0);

      /* el tapón fuera de su sitio */
      const open = s.open || 0;
      gl.uniform1f(u.uOpen, open);
      if(open > 0){
        const C = capPose(open), Cr = rot(C.rx, 0, C.rz);
        gl.uniformMatrix3fv(u.uCapRot, false, rows(Cr));    // inversa = traspuesta
        gl.uniform3fv(u.uCapOff, C.off);
      }

      /* pinzas: entran por el agujero de la base, siguiendo su eje. s.twDist es
         cuánto les falta para llegar a la boca del agujero (negativo: cuánto
         han entrado). */
      gl.uniform1f(u.uTw, s.tw ? 1 : 0);
      if(s.tw){
        const e = [0, -0.99, -0.1];                          // el agujero, espacio del objeto
        const tip = [0, 1, 2].map(i => R[i][0] * e[0] + R[i][1] * e[1] + R[i][2] * e[2] + (i === 1 ? -0.05 : 0));
        const out = [-R[0][1], -R[1][1], -R[2][1]];          // hacia fuera del agujero
        let ax = [.92 * out[0] + .16, .92 * out[1] + .12, .92 * out[2]];
        const la = Math.hypot(...ax); ax = ax.map(v => v / la);
        const pos = tip.map((v, i) => v + ax[i] * s.twDist);
        gl.uniform3fv(u.uTwPos, pos);
        gl.uniformMatrix3fv(u.uTwInv, false, twBasis(ax));
        gl.uniform1f(u.uGap, s.gap);
        gl.uniform1f(u.uTuft, s.tuft);
      }
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
  };
}

/* color de pelo "#RRGGBB" → [r, g, b] entre 0 y 1 */
const hexColor = v => {
  const m = /#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(v || "");
  return m ? [1, 2, 3].map(i => parseInt(m[i], 16) / 255) : null;
};

/* ------------------------------------------------------------
   La misma figura, ya llena, en una tarjeta (sección "Por qué").
   Vista de tres cuartos que gira apenas mientras la sección cruza
   la pantalla; fuera de pantalla no se dibuja nada.
   ------------------------------------------------------------ */
function initFiguraFija(host){
  const pieza = host.querySelector(".pieza");
  const canvas = host.querySelector("canvas");
  const low = window.matchMedia("(max-width:700px), (pointer:coarse)").matches;
  let fig = crearFiguraGL(canvas, low);
  if(!fig){
    pieza.insertAdjacentHTML("afterbegin", dogSVG(false));
    canvas.remove();
    return;
  }
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
  const scale = Math.min(window.devicePixelRatio || 1, low ? 1.25 : 1.5);
  const deg = Math.PI / 180;
  let fur = [.93, .89, .84], furDark = [.76, .71, .64], drawn = "", raf = 0, visible = false;

  function size(){
    const cs = getComputedStyle(host);
    fur = hexColor(cs.getPropertyValue("--fur")) || fur;
    furDark = hexColor(cs.getPropertyValue("--fur-dark")) || furDark;
    const w = Math.max(1, Math.round(pieza.offsetWidth * scale));
    const h = Math.max(1, Math.round(pieza.offsetHeight * scale));
    if(canvas.width !== w || canvas.height !== h){ canvas.width = w; canvas.height = h; }
    drawn = "";
    draw();
  }

  let lastScale = "";
  function draw(){
    raf = 0;
    if(!fig) return;
    const r = host.getBoundingClientRect();
    const vh = window.innerHeight;
    /* -1 al entrar por abajo, 0 en el centro, 1 al salir por arriba */
    const k = Math.max(-1, Math.min(1, ((vh / 2) - (r.top + r.height / 2)) / (vh / 2 + r.height / 2)));
    /* 1 → 1,035 → 1 al cruzar el centro; es la misma pieza, sin otro giro */
    const sc = reduce.matches ? "" : `scale(${(1 + .035 * Math.pow(Math.cos(k * Math.PI / 2), 2)).toFixed(4)})`;
    if(sc !== lastScale){ pieza.style.transform = sc; lastScale = sc; }
    const ay = reduce.matches ? -26 : -26 + 18 * k;
    const key = [ay.toFixed(2), canvas.width, canvas.height, fur.join()].join("|");
    if(key === drawn) return;
    drawn = key;
    fig.draw({ ay: ay * deg, ax: 7 * deg, az: 0, level: 1.6, phase: 0, fill: 1, fur, furDark });
  }

  const schedule = () => { if(visible && !raf) raf = requestAnimationFrame(draw); };
  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", size);
  if("IntersectionObserver" in window){
    new IntersectionObserver(es => { visible = es[0].isIntersecting; schedule(); }).observe(host);
  }else visible = true;
  canvas.addEventListener("webglcontextlost", e => { e.preventDefault(); fig = null; });
  canvas.addEventListener("webglcontextrestored", () => { fig = crearFiguraGL(canvas, low); drawn = ""; draw(); });
  size();
}

/* ------------------------------------------------------------
   La misma figura en la foto grande del bloque de compra, con el
   tono que se elige justo al lado ("Previsualiza el resultado"):
   así el cambio se ve sin volver al hero. Vista de tres cuartos
   que se gira arrastrando. Al cambiar de tono el pelo pasa al
   color nuevo; "Vacía, como llega" la vacía y, al elegir otro
   tono, se rellena. Solo se dibuja cuando algo cambia.
   ------------------------------------------------------------ */
function initFiguraCompra(host){
  const pieza = host.querySelector(".pieza");
  const canvas = host.querySelector("canvas");
  const low = window.matchMedia("(max-width:700px), (pointer:coarse)").matches;
  let fig = crearFiguraGL(canvas, low);
  if(!fig){
    pieza.insertAdjacentHTML("afterbegin", dogSVG(false));
    canvas.remove();
    return;
  }
  /* copias en 2D de cada dibujo: la miniatura de la galería (sin la cadena,
     sy = .1) y, en una columna, la figura pequeña junto a los colores */
  const galeria = host.closest(".galeria");
  const minis = [[galeria && galeria.querySelector('[data-vista="3d"] canvas'), .1],
                 [document.querySelector("[data-mini-3d]"), 0]]
    .filter(([c]) => c && c.getContext("2d"))
    .map(([c, sy]) => ({ c, sy, x: c.getContext("2d") }));
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
  const dpr = window.devicePixelRatio || 1;
  const scale = Math.min(dpr, low ? 1.25 : 1.5);
  const deg = Math.PI / 180;
  const LLENA = 1.6, VACIA = -1.25;
  const inOut = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const mezcla = (a, b, k) => a.map((v, i) => v + (b[i] - v) * k);

  /* el molde sigue a la raza elegida (app.js pone data-raza en <html>) */
  const RAZAS = { caniche: 0, pomerania: 1, golden: 2 };
  const leerRaza = () => RAZAS[document.documentElement.dataset.raza] || 0;
  let raza = leerRaza(), cambio = null;           // cambio de raza en curso: { t0, a }

  /* lo que se ve ahora y, si hay una transición en curso, de dónde a dónde va */
  let fur = [.88, .65, .37], furDark = [.69, .48, .2], level = LLENA;
  let desde = null, hasta = null, t0 = 0, dur = 0;
  let giro = 0, arrastre = null, drawn = "", raf = 0;
  /* con la figura o su copia en pantalla, los cambios se animan */
  const enVista = new Set();

  /* "Vacía" no trae color: el pelo se va con el que tenía */
  function leer(){
    const cs = getComputedStyle(host);
    const c = hexColor(cs.getPropertyValue("--fur")), d = hexColor(cs.getPropertyValue("--fur-dark"));
    return c && d ? { fur: c, furDark: d, level: LLENA } : { fur, furDark, level: VACIA };
  }
  ({ fur, furDark, level } = leer());

  function cambiar(){
    const h = leer();
    if(h.level === level && h.fur.join() === fur.join() && h.furDark.join() === furDark.join() && !hasta) return;
    /* fuera de pantalla o sin movimiento: el estado nuevo, sin transición */
    if(!enVista.size || reduce.matches){
      ({ fur, furDark, level } = h);
      desde = hasta = null;
      schedule();
      return;
    }
    desde = { fur, furDark, level };
    /* si estaba vacía, el tono nuevo entra ya y lo que se anima es el relleno */
    if(level < 0 && h.level === LLENA){ desde.fur = h.fur; desde.furDark = h.furDark; }
    hasta = h;
    dur = Math.abs(h.level - level) > .5 ? 900 : 420;
    t0 = performance.now();
    schedule();
  }

  /* otra raza: la figura gira y se apaga, cambia de molde a oscuras y
     vuelve girando al sitio (520 ms). Sin movimiento, cambio directo. */
  function cambiarRaza(){
    const a = leerRaza();
    if(a === raza && !cambio) return;
    if(!enVista.size || reduce.matches){
      raza = a; cambio = null; canvas.style.opacity = "";
      schedule();
      return;
    }
    cambio = { t0: performance.now(), a };
    schedule();
  }

  function copiarMinis(){
    const w = canvas.width, h = canvas.height;
    minis.forEach(({ c, sy, x }) => {
      if(c.width < 2) return;                      // oculta (escritorio)
      const sh = h * (1 - sy), k = Math.min(c.width / w, c.height / sh);
      x.clearRect(0, 0, c.width, c.height);
      x.drawImage(canvas, 0, h * sy, w, sh, (c.width - w * k) / 2, (c.height - sh * k) / 2, w * k, sh * k);
    });
  }

  function draw(now){
    raf = 0;
    if(!fig) return;
    let e = 1;
    if(hasta){
      e = Math.min(1, ((now || performance.now()) - t0) / dur);
      const k = inOut(e);
      fur = mezcla(desde.fur, hasta.fur, k);
      furDark = mezcla(desde.furDark, hasta.furDark, k);
      level = desde.level + (hasta.level - desde.level) * k;
      if(e >= 1) desde = hasta = null;
    }
    let extra = 0;
    if(cambio){
      const c = Math.min(1, ((now || performance.now()) - cambio.t0) / 520);
      if(c < .35){
        const k = inOut(c / .35);
        extra = 32 * k; canvas.style.opacity = (1 - k).toFixed(3);
      }else{
        raza = cambio.a;
        const k = inOut((c - .35) / .65);
        extra = -32 * (1 - k); canvas.style.opacity = k.toFixed(3);
      }
      if(c >= 1){ cambio = null; canvas.style.opacity = ""; }
    }
    /* al cambiar de tono gira un poco y vuelve: el cambio se nota de reojo */
    const ay = -26 + giro + extra + 14 * Math.sin(Math.PI * e);
    const key = [ay.toFixed(2), level.toFixed(4), fur.join(), furDark.join(), raza, canvas.width, canvas.height].join("|");
    if(key !== drawn){
      drawn = key;
      fig.draw({ ay: ay * deg, ax: 7 * deg, az: 0, level, phase: level * 6,
                 fill: level > -1.2 ? 1 : 0, fur, furDark, raza });
      copiarMinis();
    }
    if(hasta || cambio) schedule();
  }
  const schedule = () => { if(!raf) raf = requestAnimationFrame(draw); };

  function size(){
    const w = Math.max(1, Math.round(pieza.offsetWidth * scale));
    const h = Math.max(1, Math.round(pieza.offsetHeight * scale));
    if(canvas.width !== w || canvas.height !== h){ canvas.width = w; canvas.height = h; drawn = ""; }
    minis.forEach(({ c }) => {
      const mw = Math.max(1, Math.round(c.offsetWidth * dpr)), mh = Math.max(1, Math.round(c.offsetHeight * dpr));
      if(c.width !== mw || c.height !== mh){ c.width = mw; c.height = mh; drawn = ""; }
    });
    schedule();
  }

  /* Giro horizontal ilimitado: un ancho del visor equivale a una vuelta.
     Se conserva el desplazamiento vertical de la página en pantallas táctiles. */
  host.style.touchAction = "pan-y";
  host.style.cursor = "grab";
  host.addEventListener("pointerdown", e => {
    if(e.button !== 0 || arrastre) return;
    arrastre = { x: e.clientX, g: giro, id: e.pointerId };
    host.style.cursor = "grabbing";
    host.setPointerCapture(e.pointerId);
    host.classList.add("is-drag");
  });
  host.addEventListener("pointermove", e => {
    if(!arrastre || e.pointerId !== arrastre.id) return;
    giro = arrastre.g + (e.clientX - arrastre.x) * 360 / Math.max(240, host.clientWidth);
    schedule();
  });
  const suelta = e => {
    if(!arrastre || e.pointerId !== arrastre.id) return;
    arrastre = null;
    giro = ((giro % 360) + 360) % 360;
    host.style.cursor = "grab";
    host.classList.remove("is-drag");
  };
  host.addEventListener("pointerup", suelta);
  host.addEventListener("pointercancel", suelta);
  host.addEventListener("lostpointercapture", suelta);

  document.addEventListener("pk:fur", cambiar);
  document.addEventListener("pk:raza", cambiarRaza);
  window.addEventListener("resize", size);
  if("ResizeObserver" in window){
    const ro = new ResizeObserver(size);
    ro.observe(pieza);
    minis.forEach(({ c }) => ro.observe(c));
  }
  if("IntersectionObserver" in window){
    const io = new IntersectionObserver(es => es.forEach(e => {
      if(e.isIntersecting) enVista.add(e.target); else enVista.delete(e.target);
    }));
    io.observe(host);
    minis.forEach(({ c }) => io.observe(c));
  }else enVista.add(host);
  canvas.addEventListener("webglcontextlost", e => { e.preventDefault(); fig = null; });
  canvas.addEventListener("webglcontextrestored", () => { fig = crearFiguraGL(canvas, low); drawn = ""; schedule(); });
  size();
}

function dog3D(){
  return `
<div class="pieza dog" role="img" aria-label="" data-pieza>
  <canvas class="pieza__gl" data-gl aria-hidden="true"></canvas>
  <div class="pieza__sombra" data-sombra></div>
</div>`;
}

/* ------------------------------------------------------------
   Timeline del scroll. Todo sale de un único número, p (0 → 1):
   p = 0  figura vacía, de frente
   p = 1  figura llena, una vuelta completa
   Las medidas se leen solo al redimensionar; en cada frame solo
   se lee scrollY, se escriben transform/opacity y se dibuja.
   ------------------------------------------------------------ */
function initEscena(){
  const cell = document.querySelector("[data-escena]");
  if(!cell || cell.dataset.ready) return;
  const canvas = cell.querySelector("[data-gl]");
  if(!canvas) return;
  cell.dataset.ready = "1";

  const stage   = cell.querySelector(".escena");
  const mover   = cell.querySelector("[data-escena-mover]");
  /* el halo y el umbral son decoración de la escena: los pone el JS */
  const halo    = document.createElement("div");
  const umbral  = document.createElement("div");
  const sala    = document.createElement("div");
  halo.className = "escena__halo"; umbral.className = "escena__umbral"; sala.className = "escena__sala";
  [halo, umbral, sala].forEach(e => e.setAttribute("aria-hidden", "true"));
  mover.prepend(halo); stage.append(umbral); stage.prepend(sala);
  const pieza   = cell.querySelector("[data-pieza]");
  const sombra  = cell.querySelector("[data-sombra]");
  const caps    = [...cell.querySelectorAll("[data-cap]")];
  const copy    = document.querySelector(".hero__copy");
  const reduce  = window.matchMedia("(prefers-reduced-motion: reduce)");
  const low     = window.matchMedia("(max-width:700px), (pointer:coarse)").matches;

  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const smooth = t => t * t * (3 - 2 * t);
  const inOut = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const outCubic = t => 1 - Math.pow(1 - t, 3);
  /* el giro final: arranca con suavidad, avanza a ritmo constante y frena
     largo. Velocidad en trapecio (sube un 22 %, baja un 34 %): nada de
     peonza, la aceleración y la frenada quedan en los extremos. */
  const GA = .22, GB = .34, GV = 1 / (1 - (GA + GB) / 2);
  const spin = t => {
    if(t <= 0) return 0;
    if(t >= 1) return 1;
    if(t < GA) return GV * t * t / (2 * GA);
    if(t <= 1 - GB) return GV * (GA / 2 + (t - GA));
    const r = 1 - t;
    return 1 - GV * r * r / (2 * GB);
  };

  /* actos, en fracciones del recorrido p */
  const A_VACIA  = .08;   // vacía y de frente
  const A_DESTAP = .21;   // se da la vuelta, enseña la base y sale el tapón
  const A_PINZAS = .46;   // tres mechones por el agujero
  const A_CIERRE = .53;   // vuelve el tapón: clic
  const A_ENDER  = .66;   // se endereza, ya llena
  const A_GIRO   = .76;   // la vuelta final
  const A_SALIDA = .85;   // el halo se abre y aparece el umbral
  const AY_TRES = 32, VOLTEO = -112;                 // grados

  let fig = crearFiguraGL(canvas, low);
  if(!fig){
    /* sin WebGL: la figura plana de siempre, ya rellena */
    pieza.insertAdjacentHTML("afterbegin", dogSVG(false));
    canvas.remove();
  }else{
    canvas.addEventListener("webglcontextlost", e => { e.preventDefault(); fig = null; });
    canvas.addEventListener("webglcontextrestored", () => { fig = crearFiguraGL(canvas, low); drawn = null; schedule(); });
  }

  const res = resolucionLienzo(low);
  let scale = res.nitida;

  /* estado suavizado: responde al scroll en ~70 ms, sin perseguirlo */
  let cur = null, raf = 0, last = 0, drawn = null;
  /* el cierre: microcompresión de la carcasa y destello, una vez al cruzar
     A_CIERRE hacia delante. Es lo único con duración fija de la escena. */
  let snapAt = 0, snapK = 0, glintX = 0, glintA = 0;
  let pinStart = 0, pinEnd = 1, shiftFrom = 0, shiftTo = 1, shiftX = 0, shiftY = 0, pStart = 0, overlay = false;
  let fur = [.88, .65, .37], furDark = [.69, .48, .2];


  function measure(){
    const r = cell.getBoundingClientRect();
    const top = r.top + window.scrollY;
    const stickyTop = parseFloat(getComputedStyle(stage).top) || 0;
    const vh = window.innerHeight;
    pinStart = top - stickyTop;
    pinEnd = Math.max(pinStart + 1, top + cell.offsetHeight - stage.offsetHeight - stickyTop);

    /* en dos columnas la figura viaja al centro cuando el texto se va */
    const twoCols = r.left > window.innerWidth * .3;
    shiftX = 0; shiftY = 0; shiftFrom = pinStart; shiftTo = pinStart + 1; pStart = pinStart;
    /* hero a sangre: el texto va encima de la escena. La figura empieza algo
       más baja, bajo el titular, y sube al centro mientras el texto se va */
    overlay = !!copy && Math.abs(copy.getBoundingClientRect().top - r.top) < 8;
    if(overlay){
      shiftY = vh * .13;
      shiftFrom = pinStart; shiftTo = pinStart + vh * .6;
      pStart = pinStart + vh * .3;
    }else if(twoCols && copy){
      /* el centro se toma del escenario (nadie lo transforma), no del
         propio mover ni del envoltorio que mueve el puntero */
      const sr = stage.getBoundingClientRect();
      shiftX = window.innerWidth / 2 - (sr.left + sr.width / 2);
      const copyBottom = copy.getBoundingClientRect().bottom + window.scrollY;
      shiftFrom = Math.max(pinStart, copyBottom - vh * .38);
      shiftTo   = Math.max(shiftFrom + 1, copyBottom - vh * .04);
      pStart    = shiftFrom + (shiftTo - shiftFrom) * .45;
    }

    const cs = getComputedStyle(document.documentElement);
    fur = hexColor(cs.getPropertyValue("--fur")) || fur;
    furDark = hexColor(cs.getPropertyValue("--fur-dark")) || furDark;

    sizeCanvas();
    /* el primer estado se pinta ya, sin esperar a un frame */
    if(!cur){ const tg = targets(); cur = { p: tg.p, s: tg.s }; }
    apply(cur.p, cur.s, 0);
    schedule();
  }

  function sizeCanvas(){
    if(fig){
      const w = Math.max(1, Math.round(pieza.offsetWidth * scale));
      const h = Math.max(1, Math.round(pieza.offsetHeight * scale));
      if(canvas.width !== w || canvas.height !== h){ canvas.width = w; canvas.height = h; }
    }
    drawn = null;
  }

  function targets(){
    const y = window.scrollY;
    return {
      p: clamp((y - pStart) / (pinEnd - pStart)),
      s: shiftX || shiftY ? clamp((y - shiftFrom) / (shiftTo - shiftFrom)) : 1
    };
  }

  function apply(p, s, lag){
    const still = reduce.matches;
    const deg = Math.PI / 180;
    const ss = (a, b, v) => { const t = clamp((v - a) / (b - a)); return t * t * (3 - 2 * t); };

    /* desplazamiento al centro */
    const x = still ? 0 : shiftX * inOut(s);
    const yOff = still ? 0 : shiftY * (1 - inOut(s));
    mover.style.transform = x || yOff ? `translate3d(${x.toFixed(2)}px,${yOff.toFixed(2)}px,0)` : "";

    /* Actos sobre p (como se rellena la pieza de verdad):
       0    – .08  vacía y de frente; solo respira (1 → 1,012 → 1)
       .08  – .21  se da la vuelta despacio hacia la cámara y sale el tapón de la base
       .21  – .46  tres mechones entran con las pinzas por el agujero; el pelo cae
                   hacia la cabeza, que ahora está abajo, y el nivel sube un tercio
       .46  – .53  el tapón vuelve a su sitio; al cruzar .53, clic y destello
       .53  – .66  se endereza, ya llena
       .66  – .76  quieta
       .76  – 1    la vuelta completa, que acaba de frente */
    let ay = 0, flip = 0, fillP = 0, open = 0, tw = false, twDist = 3, gap = .012, tuft = 0, sc = 1, lift = 0, arc = 0;
    /* cámara y luz: se acerca y se apaga la sala mientras se rellena; al
       cerrarse y enderezarse vuelve la luz y la cámara se retira */
    const cerca = still ? 0 : smooth(clamp((p - A_VACIA) / (A_DESTAP - A_VACIA))) * (1 - smooth(clamp((p - A_CIERRE) / (A_ENDER - A_CIERRE))));
    if(still){
      fillP = 1;
    }else if(p < A_VACIA){
      sc = 1 + .012 * Math.sin(Math.PI * p / A_VACIA);
    }else if(p < A_DESTAP){
      const o = (p - A_VACIA) / (A_DESTAP - A_VACIA);
      const v = inOut(clamp(o / .72));
      ay = AY_TRES * v; flip = v;
      open = inOut(clamp((o - .62) / .38));
    }else if(p < A_PINZAS){
      ay = AY_TRES; flip = 1; open = 1;
      const u = (p - A_DESTAP) / (A_PINZAS - A_DESTAP) * 3, i = Math.min(2, Math.floor(u)), c = u - i;
      tw = true;
      if(c < .4){ twDist = 1.7 * (1 - inOut(c / .4)); tuft = 1; }
      else if(c < .6){
        twDist = -.2 * Math.sin(Math.PI * (c - .4) / .2);
        tuft = 1 - ss(.45, .56, c);
        gap = .012 + .03 * ss(.5, .6, c);
      }else{
        twDist = 1.7 * inOut((c - .6) / .4);
        gap = .042 - .03 * ss(.85, 1, c);
      }
      fillP = (i + ss(.45, .62, c)) / 3;
    }else if(p < A_CIERRE){
      ay = AY_TRES; flip = 1; fillP = 1;
      open = 1 - inOut((p - A_PINZAS) / (A_CIERRE - A_PINZAS));
    }else if(p < A_ENDER){
      fillP = 1;
      const v = inOut((p - A_CIERRE) / (A_ENDER - A_CIERRE));
      ay = AY_TRES; flip = 1 - v;
    }else if(p < A_GIRO){
      ay = AY_TRES; fillP = 1;
    }else{
      const g = (p - A_GIRO) / (1 - A_GIRO);
      ay = AY_TRES - (360 + AY_TRES) * spin(g);        // acaba en -360: de frente
      fillP = 1;
      arc = Math.sin(Math.PI * g);                     // solo durante la vuelta
      lift = -pieza.offsetHeight * .03 * arc;
      sc = 1 + .04 * arc;
    }

    sc *= 1 + .16 * cerca;
    lift -= pieza.offsetHeight * .03 * cerca;          // sube un poco: no pisa la frase de debajo
    sala.style.opacity = cerca ? cerca.toFixed(3) : "";
    sala.style.transform = x ? `translate3d(${x.toFixed(2)}px,0,0)` : "";

    /* microcompresión: scaleX 1,018 / scaleY ,985 en 100 ms, vuelve en 280 ms */
    const sx = sc * (1 + .018 * snapK), sy = sc * (1 - .015 * snapK);
    canvas.style.transform = lift || sx !== 1 || sy !== 1
      ? `translate3d(0,${lift.toFixed(2)}px,0) scale(${sx.toFixed(4)},${sy.toFixed(4)})` : "";

    if(fig){
      const state = {
        ay: ay * deg,
        /* inclinación leve de siempre; boca abajo, el volteo la sustituye */
        ax: (still ? 6 : (8 * (1 - p) + 4 + 2 * arc) * (1 - flip) + VOLTEO * flip) * deg,
        az: still ? 0 : clamp(lag * 60, -3, 3) * deg,
        /* nivel del relleno en el mundo: de lo más bajo a lo más alto de la pieza,
           así boca abajo el pelo se queda en la cabeza */
        level: -1.25 + 2.45 * fillP,
        phase: p * 40,
        fill: fillP > .002 ? 1 : 0,
        open,
        tw, twDist, gap, tuft,
        glint: glintX, glintA,
        fondo: flip,
        fur, furDark
      };
      const key = [state.ay, state.ax, state.az, state.level, open, twDist, gap, tuft, tw ? 1 : 0, glintX, glintA, canvas.width, canvas.height, fur.join()].map(v => typeof v === "number" ? v.toFixed(5) : v).join("|");
      if(key !== drawn){ fig.draw(state); drawn = key; }
    }

    sombra.style.transform = `scale(${(1 - .1 * arc).toFixed(4)},${(1 - .12 * arc).toFixed(4)})`;
    sombra.style.opacity = (1 - .25 * arc).toFixed(3);

    /* salida hacia la compra (último 15 %): el halo se abre y se dibuja el umbral */
    const out = still ? 0 : outCubic(clamp((p - A_SALIDA) / (1 - A_SALIDA)));
    halo.style.transform = out ? `scale(${(1 + .24 * out).toFixed(4)})` : "";
    /* con el titular detrás, el halo empieza tenue para no lavar las letras */
    const haloBase = overlay && !still ? .3 + .7 * smooth(s) : 1;
    halo.style.opacity = out || haloBase < 1 ? (haloBase * (1 - .2 * out)).toFixed(3) : "";
    /* el umbral va bajo la figura, que en escritorio se ha desplazado al centro */
    umbral.style.transform = `translate3d(${x.toFixed(2)}px,0,0) scaleX(${(still ? 1 : out).toFixed(4)})`;
    umbral.style.opacity = (still ? 1 : out).toFixed(3);

    /* tres frases: vacía, rellenando, cerrada y en el llavero.
       Sin movimiento las tres se ven seguidas (lo resuelve el CSS). */
    const tramos = [[0, A_VACIA], [A_VACIA, A_CIERRE], [A_CIERRE, 1]];
    caps.forEach((c, i) => {
      if(still){ c.style.opacity = ""; c.style.transform = ""; return; }
      const [a0, a1] = tramos[i] || [0, 1], mid = (a0 + a1) / 2, f = .045;
      let o = Math.min(clamp((p - a0) / f + (i === 0 ? 1 : 0)), clamp((a1 - p) / f + (i === caps.length - 1 ? 1 : 0)));
      o *= smooth(s);
      c.style.opacity = o.toFixed(3);
      c.style.transform = `translate3d(0,${((1 - o) * (p < mid ? 10 : -10)).toFixed(2)}px,0)`;
    });
  }

  /* el cierre avanza con el reloj: compresión 100 ms, vuelta 280 ms, destello 350 ms */
  function tickSnap(t){
    if(!snapAt) return false;
    const e = t - snapAt;
    if(e >= 380){ snapAt = 0; snapK = 0; glintA = 0; return false; }
    snapK = e < 100 ? outCubic(e / 100) : 1 - (1 - Math.pow(1 - (e - 100) / 280, 4));
    const g = clamp(e / 350);
    glintX = -1.25 + 2.9 * outCubic(g);
    glintA = e < 350 ? .9 * Math.sin(Math.PI * g) : 0;
    return true;
  }

  /* un solo requestAnimationFrame para la escena: frame() también mueve el cierre */
  function frame(t){
    const raw = last ? t - last : 16;
    const dt = Math.min(64, raw);
    last = t;
    if(fig) res.medir(raw);
    const tg = targets();
    if(!cur) cur = { p: tg.p, s: tg.s };
    const k = reduce.matches ? 1 : 1 - Math.exp(-dt / 70);
    const prev = cur.p;
    cur.p += (tg.p - cur.p) * k;
    cur.s += (tg.s - cur.s) * k;
    const done = Math.abs(tg.p - cur.p) < 1e-4 && Math.abs(tg.s - cur.s) < 1e-4;
    if(done){ cur.p = tg.p; cur.s = tg.s; }
    /* se cierra al cruzar hacia delante; hacia atrás no se "abre" con destello */
    if(!reduce.matches && prev < A_CIERRE && cur.p >= A_CIERRE) snapAt = t;
    const snapping = tickSnap(t);
    const quiere = res.para(!done || snapping);
    if(fig && quiere !== scale){ scale = quiere; sizeCanvas(); }
    apply(cur.p, cur.s, done ? 0 : tg.p - cur.p);
    if(done && !snapping){ raf = 0; last = 0; }
    else raf = requestAnimationFrame(frame);
  }

  function schedule(){ if(!raf) raf = requestAnimationFrame(frame); }

  /* el previsualizador de color cambia --fur: la figura lo relee sin esperar a un resize */
  function readFur(){
    const cs = getComputedStyle(document.documentElement);
    fur = hexColor(cs.getPropertyValue("--fur")) || fur;
    furDark = hexColor(cs.getPropertyValue("--fur-dark")) || furDark;
    drawn = null;
    schedule();
  }
  document.addEventListener("pk:fur", readFur);

  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", measure);
  if("ResizeObserver" in window) new ResizeObserver(measure).observe(cell);
  if(document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  reduce.addEventListener && reduce.addEventListener("change", measure);
  measure();
}

/* ============================================================
   El llavero de huella en 3D, segundo hero.

   Piel sintética plana con la silueta de una huella y la huella
   troquelada: cuatro dedos y la almohadilla. Detrás de las ventanas,
   una lámina transparente y un bolsillo; el pelo se ve a través.
   Cuelga de la anilla: arriba, la lengüeta que abraza la anilla y se
   cierra con un botón a presión. Al desabrocharla queda a la vista la
   boca del bolsillo, en el canto de arriba, y por ahí entra el pelo.

   Espacio del objeto: x a lo ancho, y hacia arriba (colgado), z hacia
   la cámara. La huella se dibuja "de pie" (dedos arriba) en up(), que
   la da la vuelta: colgada, la almohadilla queda junto a la lengüeta.
   ============================================================ */
const HUELLA_FS = `
precision highp float;
uniform vec2  uRes;
uniform mat3  uRot;     // objeto -> mundo
uniform mat3  uInv;     // mundo -> objeto
uniform float uLevel;   // el pelo ocupa lo que queda por encima (y del objeto): entra por arriba
uniform float uFill;    // 0 = vacío
uniform vec3  uFur;
uniform vec3  uFurDark;
uniform vec3  uCuero;   // color de la piel
uniform float uOpen;    // 0 = abrochada; 1 = lengüeta levantada
uniform float uTuft;    // tamaño del mechón que entra (0 = no hay)
uniform vec3  uTuftPos; // su centro (objeto)
uniform float uGlint;
uniform float uGlintA;

const float T = 0.075;      // media altura de la pieza
const float SUELO = -0.03;  // cara de dentro de la piel de atrás
const float FILM = 0.022;   // la lámina transparente, bajo la piel de delante

${PELO_GLSL}

float sdEllipsoid(vec3 p, vec3 r){
  float k0 = length(p / r);
  float k1 = length(p / (r * r));
  return k0 * (k0 - 1.0) / max(k1, 1e-4);
}
float sdEll2(vec2 p, vec2 r){
  float k0 = length(p / r);
  float k1 = length(p / (r * r));
  return k0 * (k0 - 1.0) / max(k1, 1e-4);
}
float smin(float a, float b, float k){
  float h = max(k - abs(a - b), 0.0) / k;
  return min(a, b) - h * h * k * 0.25;
}
float smax(float a, float b, float k){ return -smin(-a, -b, k); }
vec2 rot2(vec2 p, float a){ float c = cos(a), s = sin(a); return vec2(c * p.x + s * p.y, -s * p.x + c * p.y); }
float rbox2(vec2 p, vec2 b, float r){ vec2 q = abs(p) - b + r; return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r; }
float losa(float d2, float z, float h, float r){
  vec2 w = vec2(d2 + r, abs(z) - (h - r));
  return min(max(w.x, w.y), 0.0) + length(max(w, 0.0)) - r;
}

/* de colgado a "de pie": la huella con los dedos arriba */
vec2 up(vec2 p){ return vec2(p.x, -p.y - 0.064); }

/* Silueta, ajustada sobre la foto del proveedor (error medio < 2 px):
   cuatro lóbulos redondos, uno por dedo, con valles marcados, y abajo
   la parte de la almohadilla, ancha y de esquinas redondas. k1 y k2
   suavizan los valles; la costura usa unos más blandos. */
float contornoK(vec2 q, float k1, float k2){
  vec2 a = vec2(abs(q.x), q.y);
  float d = smin(length(a - vec2(0.1711, 0.4654)) - 0.375,
                 length(a - vec2(0.4602, 0.0792)) - 0.422, k1);
  d = smin(d, rbox2(q - vec2(0.0, -0.5055), vec2(0.563, 0.2785), 0.236), k2);
  return min(d, length(q) - 0.521);
}
float contorno(vec2 q){ return contornoK(q, 0.063, 0.049); }
/* la huella troquelada: cuatro dedos ovalados y la almohadilla, de cúpula
   arriba y tres lóbulos suaves abajo */
float ventanas(vec2 q){
  vec2 a = vec2(abs(q.x), q.y);
  float d = sdEll2(rot2(a - vec2(0.205, 0.365), -0.1), vec2(0.155, 0.205));
  d = min(d, sdEll2(rot2(a - vec2(0.47, 0.045), -0.45), vec2(0.15, 0.19)));
  float pad = sdEll2(q - vec2(0.0, -0.14), vec2(0.26, 0.25));
  pad = smin(pad, sdEll2(a - vec2(0.21, -0.34), vec2(0.17, 0.15)), 0.14);
  pad = smin(pad, sdEll2(q - vec2(0.0, -0.39), vec2(0.15, 0.12)), 0.1);
  return min(d, pad);
}

/* la pieza de piel: losa redondeada, con la huella troquelada hasta el
   fondo del bolsillo y, abierta, la boca del bolsillo en el canto de arriba */
float cuero(vec3 p){
  vec2 q = up(p.xy);
  float d = losa(contorno(q), p.z, T, 0.03);
  if(d > 0.25) return d;
  d = smax(d, -max(ventanas(q), SUELO - p.z), 0.012);
  if(uOpen > 0.0){
    float boca = max(max(abs(p.x) - 0.42, 0.6 - p.y), max(SUELO - p.z, p.z - FILM));
    d = smax(d, -boca, 0.01);
  }
  return d;
}

/* La lengüeta: una tira que sale de detrás, abraza la anilla por arriba y
   baja por delante hasta el botón, con la punta redonda alrededor de él.
   La parte de delante gira sobre el pliegue al desabrocharla. */
const float YF = 1.0;                       // el pliegue
const vec2 BOTON = vec2(0.0, 0.68);         // el botón, algo dentro del canto
const vec3 BISAGRA = vec3(0.0, YF, 0.0935);
float tira(vec3 p, float z0, float z1){
  float f = smin(length(p.xy - BOTON) - 0.245,
                 max(rbox2(p.xy - vec2(0.0, (BOTON.y + YF) * 0.5 + 0.02), vec2(0.21, (YF - BOTON.y) * 0.5 + 0.02), 0.03), p.y - YF), 0.1);
  vec2 w = vec2(f, abs(p.z - (z0 + z1) * 0.5) - (z1 - z0) * 0.5 + 0.006);
  return min(max(w.x, w.y), 0.0) + length(max(w, 0.0)) - 0.006;
}
vec3 aDelante(vec3 p){
  /* de la posición abierta a la cerrada: girar uOpen·150° sobre el pliegue */
  float a = uOpen * 2.62, c = cos(a), s = sin(a);
  vec3 q = p - BISAGRA;
  return vec3(q.x, c * q.y - s * q.z, s * q.y + c * q.z) + BISAGRA;
}
float lengueta(vec3 p){
  float d = tira(p, -0.112, -0.075);                               // por detrás
  vec2 c = vec2(p.y - YF, p.z);
  float pl = max(abs(length(c) - 0.0935) - 0.0185, -(p.y - YF));   // el pliegue
  pl = max(pl, abs(p.x) - 0.21);
  d = min(d, pl);
  return min(d, tira(aDelante(p), 0.075, 0.112));                  // por delante
}
/* metal: la anilla partida (dos vueltas de fleje plano, una encima de la
   otra; la de delante se corta donde empieza la segunda vuelta), apoyada
   por dentro del pliegue; la tapa del botón, un ojal cromado con el
   centro hundido, y el macho en la piel cuando está abierta */
const float RA = 0.38;                                   // radio de la anilla (al centro del fleje)
const vec3 CA = vec3(0.0, YF + 0.051 + RA, 0.0);
float anilla(vec3 p){
  vec3 v = p - CA;
  float r = length(v.xy) - RA;
  if(abs(r) > 0.08 || abs(v.z) > 0.08) return max(length(vec2(r, v.z)) - 0.03, 0.02);
  float atras = rbox2(vec2(r, v.z + 0.0098), vec2(0.021, 0.0092), 0.008);
  float alante = rbox2(vec2(r, v.z - 0.0098), vec2(0.021, 0.0092), 0.008);
  /* el corte de la vuelta de delante, arriba a la izquierda */
  float a = atan(v.x, v.y) + 0.72;
  a = abs(a - 6.2832 * floor(a / 6.2832 + 0.5));
  alante = max(alante, 0.035 - a * RA);
  return min(atras, alante);
}
float metal(vec3 p){
  float d = anilla(p);
  vec3 c = aDelante(p) - vec3(BOTON, 0.112);
  float rho = length(c.xy);
  if(rho < 0.2){
    d = min(d, length(vec2(rho - 0.118, c.z - 0.008)) - 0.026);            // el aro
    d = min(d, max(rho - 0.12, abs(c.z + 0.004 - 0.35 * rho * rho) - 0.007)); // el centro, hundido
  }
  if(uOpen > 0.0){
    vec3 m = p - vec3(BOTON, 0.075);
    d = min(d, sdEllipsoid(m, vec3(0.06, 0.06, 0.03)));
    d = min(d, length(vec2(length(m.xy) - 0.1, m.z)) - 0.012);
  }
  return d;
}

/* el pelo dentro del bolsillo, apretado contra la lámina; solo hace falta
   donde se ve, bajo las ventanas. El frente del relleno baja con uLevel. */
float peloDentro(vec3 p){
  vec2 q = up(p.xy);
  float v = ventanas(q) - 0.02;
  float top = FILM - 0.004 - 0.008 * vnoise(p * 19.0);
  float d = max(v, max(p.z - top, SUELO - p.z));
  float frente = uLevel - p.y + 0.06 * (vnoise(vec3(p.x * 6.0, p.y * 2.5, 3.0)) - 0.5);
  return max(d, frente);
}
float mechon(vec3 p){
  if(uTuft < 0.01) return 9.0;
  vec3 r = vec3(0.16, 0.2, 0.1) * uTuft;
  return sdEllipsoid(p - uTuftPos, r) - 0.012 * uTuft * vnoise(p * 30.0);
}

float mapO(vec3 p, out float m){
  float d = cuero(p); m = 0.0;
  float l = lengueta(p); if(l < d){ d = l; m = 4.0; }
  float g = metal(p);    if(g < d){ d = g; m = 1.0; }
  if(uFill > 0.0){ float h = peloDentro(p); if(h < d){ d = h; m = 2.0; } }
  float t = mechon(p);   if(t < d){ d = t; m = 3.0; }
  return d;
}
float mapD(vec3 p){ float m; return mapO(p, m); }
vec3 nrm(vec3 p){
  vec2 e = vec2(0.0015, -0.0015);
  return normalize(e.xyy * mapD(p + e.xyy) + e.yyx * mapD(p + e.yyx) +
                   e.yxy * mapD(p + e.yxy) + e.xxx * mapD(p + e.xxx));
}

vec3 env(vec3 d){
  vec3 c = mix(vec3(0.50, 0.46, 0.42), vec3(1.0, 0.99, 0.97), smoothstep(-0.25, 0.85, d.y));
  c *= mix(0.62, 1.0, smoothstep(-0.8, 0.1, d.y));
  c += 2.0 * pow(max(dot(d, normalize(vec3(-0.55, 0.62, 0.56))), 0.0), 36.0);
  c += 0.9 * smoothstep(0.90, 0.985, dot(d, normalize(vec3(0.92, 0.12, 0.38))));
  return c;
}

const vec3 L1 = vec3(-0.5302, 0.6266, 0.5302);
const vec3 L2 = vec3(0.8549, 0.2137, 0.4720);

/* piel sintética: grano fino, satinado ancho y un reflejo flojo del estudio.
   El canto va algo más oscuro (tintado) y el pespunte, tono sobre tono. */
vec3 pielCol(vec3 p, vec3 n, vec3 rd, float sombra, bool costura){
  vec3 no = uInv * n;
  float g = vnoise(p * 42.0) * 0.6 + vnoise(p * 97.0) * 0.4;
  vec3 nb = normalize(n + uRot * (vec3(vnoise(p * 60.0 + 3.1), vnoise(p * 60.0 + 7.7), 0.0) - 0.5) * 0.12);
  float d1 = clamp((dot(nb, L1) + 0.2) / 1.2, 0.0, 1.0);
  float d2 = clamp(dot(nb, L2), 0.0, 1.0);
  vec3 base = uCuero * (0.94 + 0.12 * g);
  if(abs(no.z) < 0.55) base *= 0.82;                       // canto
  vec2 q = up(p.xy);
  if(costura && abs(no.z) > 0.8){
    /* pespunte de hilo oscuro, a 0,06 del canto, puntadas largas */
    float s = abs(contornoK(q, 0.16, 0.16) + 0.06);
    float ang = atan(q.y, q.x);
    float dash = smoothstep(0.3, 0.36, fract(ang * 13.0)) * (1.0 - smoothstep(0.94, 1.0, fract(ang * 13.0)));
    base *= 1.0 - 0.2 * (1.0 - smoothstep(0.006, 0.02, s));      // la pisada del pespunte
    base = mix(base, uCuero * 0.42, (1.0 - smoothstep(0.0045, 0.0085, s)) * dash);
  }
  vec3 col = base * (0.40 + 0.6 * d1 + 0.16 * d2) * sombra;
  vec3 h = normalize(L1 - rd);
  /* satinado: un brillo ancho y otro más apretado, como la piel sintética lisa */
  float sp = pow(max(dot(nb, h), 0.0), 18.0) * (0.10 + 0.06 * g) + pow(max(dot(nb, h), 0.0), 70.0) * 0.18;
  float F = 0.06 + 0.94 * pow(1.0 - clamp(dot(-rd, nb), 0.0, 1.0), 5.0);
  return col + vec3(sp) * sombra + env(reflect(rd, nb)) * F * 0.22 * sombra;
}
/* níquel pulido: refleja un estudio de contraste alto (dos cajas de luz,
   techo claro, suelo oscuro), que es lo que hace que se lea como metal */
vec3 estudio(vec3 d){
  float y = d.y;
  vec3 c = mix(vec3(0.26, 0.26, 0.27), vec3(0.66, 0.66, 0.68), smoothstep(-0.35, 0.05, y));
  c = mix(c, vec3(0.95, 0.95, 0.97), smoothstep(0.25, 0.8, y));
  c *= 0.7 + 0.3 * smoothstep(0.0, 0.14, abs(y + 0.04));
  c += 2.4 * smoothstep(0.86, 0.95, dot(d, normalize(vec3(-0.55, 0.62, 0.56))));
  c += 1.3 * smoothstep(0.9, 0.97, dot(d, normalize(vec3(0.92, 0.12, 0.38))));
  c += 0.75 * smoothstep(0.55, 0.92, dot(d, normalize(vec3(-0.15, 0.2, 1.0))));   // la caja grande de frente
  c += 1.1 * smoothstep(0.8, 0.95, dot(d, normalize(vec3(0.25, -0.6, 0.75))));   // y un rebote claro abajo
  return c;
}
vec3 metalCol(vec3 n, vec3 rd){
  float cosi = clamp(dot(-rd, n), 0.0, 1.0);
  vec3 refl = reflect(rd, n);
  float F = 0.72 + 0.28 * pow(1.0 - cosi, 5.0);
  vec3 col = estudio(refl) * vec3(0.84, 0.85, 0.88) * F;
  return col + vec3(1.0) * pow(max(dot(refl, L1), 0.0), 90.0) * 1.5;
}
vec3 peloShade(vec3 p, vec3 n, float sombra){
  float mech; vec3 dir;
  vec2 h = pelo(p * 1.4, uInv * n, mech, dir);
  float d1 = clamp((dot(n, L1) + 0.45) / 1.45, 0.0, 1.0);
  return peloCol(h, mech) * (0.5 + 0.5 * d1) * sombra;
}

float glint(vec3 p){
  if(uGlintA <= 0.0) return 0.0;
  float s = dot(p, vec3(0.62, 0.74, 0.0));
  float b = (s - uGlint) / 0.12;
  return exp(-b * b) * uGlintA;
}

void main(){
  vec2 uv = gl_FragCoord.xy / uRes * 2.0 - 1.0;
  float aspect = uRes.x / uRes.y;
  vec3 ro = vec3(0.0, 0.42, 7.0);
  float tanH = 1.62 / 7.0;
  vec3 rd = normalize(vec3(uv.x * tanH * aspect, uv.y * tanH, -1.0));
  vec3 roO = uInv * ro, rdO = uInv * rd;

  vec3 oc = roO - vec3(0.0, 0.42, 0.0);
  float bb = dot(oc, rdO), cc = dot(oc, oc) - 1.8 * 1.8, hh = bb * bb - cc;
  if(hh < 0.0){ gl_FragColor = vec4(0.0); return; }
  hh = sqrt(hh);
  float t = max(-bb - hh, 0.0), tEnd = -bb + hh;
  float m = 0.0; bool hit = false;
  for(int i = 0; i < STEPS; i++){
    float d = mapO(roO + rdO * t, m);
    if(d < 0.0012){ hit = true; break; }
    t += d * 0.9;
    if(t > tEnd) break;
  }
  if(!hit){ gl_FragColor = vec4(0.0); return; }

  vec3 p = roO + rdO * t;
  vec3 nO = nrm(p);
  vec3 n = uRot * nO;
  vec2 q = up(p.xy);
  /* dentro del bolsillo (entre el fondo y la lámina) llega menos luz junto a
     las paredes de la huella; la cara de atrás de la pieza no cuenta */
  float w = ventanas(q);
  bool dentro = p.z > SUELO - 0.004 && p.z < FILM + 0.002;
  float hondo = dentro ? mix(0.5, 1.0, clamp(-w / 0.07, 0.0, 1.0)) : 1.0;
  /* con la lengüeta fuera, la boca del bolsillo, en el canto de arriba, es oscura */
  if(dentro && uOpen > 0.0 && p.y > 0.56 && abs(p.x) < 0.44) hondo *= mix(1.0, 0.3, smoothstep(0.56, 0.68, p.y));
  vec3 col;
  if(m > 3.5)      col = pielCol(p, n, rd, 1.0, false) * 0.97;     // la lengüeta, misma piel
  else if(m > 2.5) col = peloShade(p, n, 1.0);
  else if(m > 1.5) col = peloShade(p, n, hondo);
  else if(m > 0.5) col = metalCol(n, rd);
  else if(dentro && abs(p.z - SUELO) < 0.006 && w < 0.0){
    /* el fondo, vacío: el revés de la piel sintética, un tejido claro */
    float d1 = clamp((dot(n, L1) + 0.3) / 1.3, 0.0, 1.0);
    float tej = 0.88 + 0.12 * vnoise(p * vec3(160.0, 160.0, 1.0)) * vnoise(p * vec3(40.0, 300.0, 1.0));
    col = vec3(0.86, 0.83, 0.78) * tej * (0.55 + 0.45 * d1) * hondo;
  }
  else             col = pielCol(p, n, rd, dentro ? hondo * 0.7 : 1.0, !dentro);

  float gl = glint(p);
  /* la lámina transparente: solo existe dentro de las ventanas y de frente */
  if(abs(rdO.z) > 1e-4){
    float tf = (FILM - roO.z) / rdO.z;
    vec3 pf = roO + rdO * tf;
    if(tf > 0.0 && tf < t && ventanas(up(pf.xy)) < 0.0){
      vec3 nf = uRot * vec3(0.0, 0.0, rdO.z < 0.0 ? 1.0 : -1.0);
      vec3 refl = reflect(rd, nf);
      float F = 0.05 + 0.95 * pow(1.0 - clamp(dot(-rd, nf), 0.0, 1.0), 5.0);
      col = col * (1.0 - F) * 0.95 + env(refl) * F * 0.9;
      col += vec3(1.0) * (pow(max(dot(refl, L1), 0.0), 120.0) * 1.4 + pow(max(dot(refl, L2), 0.0), 60.0) * 0.35);
      gl *= 2.0;
    }
  }
  col += vec3(1.0, 0.99, 0.96) * gl * 0.35;
  /* la anilla se funde hacia el borde de arriba */
  float y = gl_FragCoord.y / uRes.y;
  gl_FragColor = vec4(col, 1.0) * (1.0 - smoothstep(0.965, 1.0, y));
}`;

function rotXYZ(ax, ay, az){
  const [cx, sx, cy, sy, cz, sz] = [Math.cos(ax), Math.sin(ax), Math.cos(ay), Math.sin(ay), Math.cos(az), Math.sin(az)];
  const ry = [[cy, 0, sy], [0, 1, 0], [-sy, 0, cy]];
  const rx = [[1, 0, 0], [0, cx, -sx], [0, sx, cx]];
  const rz = [[cz, -sz, 0], [sz, cz, 0], [0, 0, 1]];
  const mul = (A, B) => A.map((r, i) => [0, 1, 2].map(j => r[0] * B[0][j] + r[1] * B[1][j] + r[2] * B[2][j]));
  return mul(mul(rx, ry), rz);
}

function crearHuellaGL(canvas, low){
  const gl = canvas.getContext("webgl", {
    alpha: true, premultipliedAlpha: true, antialias: false,
    depth: false, stencil: false, powerPreference: "high-performance"
  });
  if(!gl) return null;
  const sh = (type, src) => {
    const o = gl.createShader(type);
    gl.shaderSource(o, src);
    gl.compileShader(o);
    if(!gl.getShaderParameter(o, gl.COMPILE_STATUS)){
      console.warn("huella 3D:", gl.getShaderInfoLog(o));
      return null;
    }
    return o;
  };
  const defs = low ? "#define STEPS 64\n#define LOW 1\n" : "#define STEPS 88\n";
  const vs = sh(gl.VERTEX_SHADER, FIGURA_VS);
  const fs = sh(gl.FRAGMENT_SHADER, defs + HUELLA_FS);
  if(!vs || !fs) return null;
  const prog = gl.createProgram();
  gl.attachShader(prog, vs); gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if(!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
  gl.useProgram(prog);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const aLoc = gl.getAttribLocation(prog, "a");
  gl.enableVertexAttribArray(aLoc);
  gl.vertexAttribPointer(aLoc, 2, gl.FLOAT, false, 0, 0);
  const u = {};
  ["uRes", "uRot", "uInv", "uLevel", "uFill", "uFur", "uFurDark", "uCuero",
   "uOpen", "uTuft", "uTuftPos", "uGlint", "uGlintA"].forEach(n => u[n] = gl.getUniformLocation(prog, n));
  const cols = M => new Float32Array([M[0][0], M[1][0], M[2][0], M[0][1], M[1][1], M[2][1], M[0][2], M[1][2], M[2][2]]);
  const rows = M => new Float32Array([M[0][0], M[0][1], M[0][2], M[1][0], M[1][1], M[1][2], M[2][0], M[2][1], M[2][2]]);
  return {
    gl,
    draw(s){
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(u.uRes, canvas.width, canvas.height);
      const R = rotXYZ(s.ax, s.ay, s.az || 0);
      gl.uniformMatrix3fv(u.uRot, false, cols(R));
      gl.uniformMatrix3fv(u.uInv, false, rows(R));
      gl.uniform1f(u.uLevel, s.level);
      gl.uniform1f(u.uFill, s.fill);
      gl.uniform3fv(u.uFur, s.fur);
      gl.uniform3fv(u.uFurDark, s.furDark);
      gl.uniform3fv(u.uCuero, s.cuero);
      gl.uniform1f(u.uOpen, s.open || 0);
      gl.uniform1f(u.uTuft, s.tuft || 0);
      gl.uniform3fv(u.uTuftPos, s.tuftPos || [0, 9, 0]);
      gl.uniform1f(u.uGlint, s.glint || 0);
      gl.uniform1f(u.uGlintA, s.glintA || 0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
  };
}

/* los cuatro colores que se venden, en el orden de la tienda */
const CUEROS = {
  negro:  [.11, .105, .102],
  azul:   [.12, .15, .27],
  gris:   [.58, .58, .57],
  beige:  [.84, .74, .60]
};
const CUERO_ORDEN = ["negro", "azul", "gris", "beige"];

function huella3D(){
  return `
<div class="pieza pieza--huella" data-pieza>
  <canvas class="pieza__gl" data-gl aria-hidden="true"></canvas>
  <div class="pieza__sombra" data-sombra></div>
</div>`;
}

/* ------------------------------------------------------------
   Timeline del segundo hero, con el mismo motor que la figura:
   p = 0  el llavero colgado, vacío y de frente
   p = 1  lleno, cerrado y tras una vuelta en la que pasa por los
          cuatro colores (si no se ha elegido uno)
   ------------------------------------------------------------ */
function initEscenaHuella(){
  const cell = document.querySelector("[data-escena-huella]");
  if(!cell || cell.dataset.ready) return;
  const canvas = cell.querySelector("[data-gl]");
  if(!canvas) return;
  cell.dataset.ready = "1";

  const section = cell.closest("section");
  const stage   = cell.querySelector(".escena");
  const mover   = cell.querySelector("[data-escena-mover]");
  const halo    = document.createElement("div");
  const umbral  = document.createElement("div");
  const sala    = document.createElement("div");
  halo.className = "escena__halo"; umbral.className = "escena__umbral"; sala.className = "escena__sala";
  [halo, umbral, sala].forEach(e => e.setAttribute("aria-hidden", "true"));
  mover.prepend(halo); stage.append(umbral); stage.prepend(sala);
  const pieza   = cell.querySelector("[data-pieza]");
  const sombra  = cell.querySelector("[data-sombra]");
  const caps    = [...cell.querySelectorAll("[data-cap]")];
  const copy    = section && section.querySelector(".hero__copy");
  const swatches = section ? [...section.querySelectorAll("[data-cuero]")] : [];
  const nombre  = section && section.querySelector("[data-cuero-nombre]");
  const reduce  = window.matchMedia("(prefers-reduced-motion: reduce)");
  const low     = window.matchMedia("(max-width:700px), (pointer:coarse)").matches;

  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const smooth = t => t * t * (3 - 2 * t);
  const inOut = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const outCubic = t => 1 - Math.pow(1 - t, 3);
  const mezcla = (a, b, k) => a.map((v, i) => v + (b[i] - v) * k);
  const GA = .22, GB = .34, GV = 1 / (1 - (GA + GB) / 2);
  const spin = t => {
    if(t <= 0) return 0;
    if(t >= 1) return 1;
    if(t < GA) return GV * t * t / (2 * GA);
    if(t <= 1 - GB) return GV * (GA / 2 + (t - GA));
    const r = 1 - t;
    return 1 - GV * r * r / (2 * GB);
  };

  /* actos */
  const A_VACIO  = .08;   // colgado, vacío, de frente
  const A_ABRE   = .22;   // se inclina hacia la cámara y se desabrocha la lengüeta
  const A_PELO   = .48;   // tres mechones entran por la boca de arriba
  const A_CIERRE = .55;   // la lengüeta baja: clic
  const A_ENDER  = .66;   // vuelve de frente
  const A_GIRO   = .74;   // la vuelta, pasando por los cuatro colores
  const A_SALIDA = .86;
  const AY_TRES = -22, INCLINA = 40;                     // grados
  const LLENO = -1.05, VACIO = .8;                       // nivel del relleno (y del objeto)

  let fig = crearHuellaGL(canvas, low);
  if(!fig){
    pieza.insertAdjacentHTML("afterbegin", `<img src="${cell.dataset.fallback || ""}" alt="" class="pieza__foto">`);
    canvas.remove();
  }else{
    canvas.addEventListener("webglcontextlost", e => { e.preventDefault(); fig = null; });
    canvas.addEventListener("webglcontextrestored", () => { fig = crearHuellaGL(canvas, low); drawn = null; schedule(); });
  }

  const res = resolucionLienzo(low);
  let scale = res.nitida;
  let cur = null, raf = 0, last = 0, drawn = null;
  let snapAt = 0, snapK = 0, glintX = 0, glintA = 0;
  let pinStart = 0, pinEnd = 1, shiftFrom = 0, shiftTo = 1, shiftX = 0, shiftY = 0, pStart = 0, overlay = false;
  let fur = [.88, .65, .37], furDark = [.69, .48, .2];
  /* color: el que se elige con las muestras manda; sin elegir, la vuelta los enseña todos */
  let elegido = null, cueroVisto = "negro", cambioColor = null;   // cambioColor: { de, a, t0 }

  function measure(){
    const r = cell.getBoundingClientRect();
    const top = r.top + window.scrollY;
    const stickyTop = parseFloat(getComputedStyle(stage).top) || 0;
    const vh = window.innerHeight;
    pinStart = top - stickyTop;
    pinEnd = Math.max(pinStart + 1, top + cell.offsetHeight - stage.offsetHeight - stickyTop);
    const twoCols = r.left > window.innerWidth * .3;
    shiftX = 0; shiftY = 0; shiftFrom = pinStart; shiftTo = pinStart + 1; pStart = pinStart;
    overlay = !!copy && Math.abs(copy.getBoundingClientRect().top - r.top) < 8;
    if(overlay){
      shiftY = vh * .13;
      shiftFrom = pinStart; shiftTo = pinStart + vh * .6;
      pStart = pinStart + vh * .3;
    }else if(twoCols && copy){
      const sr = stage.getBoundingClientRect();
      shiftX = window.innerWidth / 2 - (sr.left + sr.width / 2);
      const copyBottom = copy.getBoundingClientRect().bottom + window.scrollY;
      shiftFrom = Math.max(pinStart, copyBottom - vh * .38);
      shiftTo   = Math.max(shiftFrom + 1, copyBottom - vh * .04);
      pStart    = shiftFrom + (shiftTo - shiftFrom) * .45;
    }
    readFur();
    sizeCanvas();
    if(!cur){ const tg = targets(); cur = { p: tg.p, s: tg.s }; }
    apply(cur.p, cur.s, 0);
    schedule();
  }

  function sizeCanvas(){
    if(fig){
      const w = Math.max(1, Math.round(pieza.offsetWidth * scale));
      const h = Math.max(1, Math.round(pieza.offsetHeight * scale));
      if(canvas.width !== w || canvas.height !== h){ canvas.width = w; canvas.height = h; }
    }
    drawn = null;
  }

  function targets(){
    const y = window.scrollY;
    return {
      p: clamp((y - pStart) / (pinEnd - pStart)),
      s: shiftX || shiftY ? clamp((y - shiftFrom) / (shiftTo - shiftFrom)) : 1
    };
  }

  /* el color que toca en este punto del recorrido */
  function colorDe(p){
    if(elegido) return { rgb: CUEROS[elegido], nombre: elegido };
    if(p < A_GIRO) return { rgb: CUEROS.negro, nombre: "negro" };
    /* cambia cuando la pieza está de canto o de espaldas: el cambio no se ve
       hacer, se ve hecho. 4 colores en una vuelta, acaba en beige */
    const g = spin((p - A_GIRO) / (1 - A_GIRO)), x = g * 4;
    const tramo = x - Math.floor(x);
    const a = CUERO_ORDEN[Math.min(3, Math.floor(x))], b = CUERO_ORDEN[Math.min(3, Math.floor(x) + 1)];
    const k = smooth(clamp((tramo - .35) / .3));
    return { rgb: mezcla(CUEROS[a], CUEROS[b], k), nombre: k > .5 ? b : a };
  }

  function apply(p, s, lag){
    const still = reduce.matches;
    const deg = Math.PI / 180;
    const ss = (a, b, v) => { const t = clamp((v - a) / (b - a)); return t * t * (3 - 2 * t); };
    const x = still ? 0 : shiftX * inOut(s);
    const yOff = still ? 0 : shiftY * (1 - inOut(s));
    mover.style.transform = x || yOff ? `translate3d(${x.toFixed(2)}px,${yOff.toFixed(2)}px,0)` : "";

    let ay = 0, ax = 0, fillP = 0, open = 0, tuft = 0, tuftPos = [0, 9, 0], sc = 1, lift = 0, arc = 0;
    const cerca = still ? 0 : smooth(clamp((p - A_VACIO) / (A_ABRE - A_VACIO))) * (1 - smooth(clamp((p - A_CIERRE) / (A_ENDER - A_CIERRE))));
    if(still){
      fillP = 1;
    }else if(p < A_VACIO){
      sc = 1 + .012 * Math.sin(Math.PI * p / A_VACIO);
      ay = 6 * Math.sin(Math.PI * p / A_VACIO);           // se mece un poco en la anilla
    }else if(p < A_ABRE){
      const o = (p - A_VACIO) / (A_ABRE - A_VACIO);
      const v = inOut(clamp(o / .7));
      ay = AY_TRES * v; ax = INCLINA * v;
      open = inOut(clamp((o - .55) / .45));
    }else if(p < A_PELO){
      ay = AY_TRES; ax = INCLINA; open = 1;
      const u = (p - A_ABRE) / (A_PELO - A_ABRE) * 3, i = Math.min(2, Math.floor(u)), c = u - i;
      const x0 = [-.28, .26, -.04][i];
      /* baja desde arriba hasta la boca (0 → .5), entra encogiéndose (.5 → .75) */
      const baja = inOut(clamp(c / .5)), entra = ss(.5, .75, c);
      const yy = 2.15 - (2.15 - .74) * baja - .22 * entra;
      tuftPos = [x0, yy, .12 * (1 - baja) - .004];
      tuft = (c < .5 ? smooth(clamp(c / .12)) : 1) * (1 - entra);
      fillP = (i + ss(.55, .9, c)) / 3;
    }else if(p < A_CIERRE){
      ay = AY_TRES; ax = INCLINA; fillP = 1;
      open = 1 - inOut((p - A_PELO) / (A_CIERRE - A_PELO));
    }else if(p < A_ENDER){
      fillP = 1;
      const v = inOut((p - A_CIERRE) / (A_ENDER - A_CIERRE));
      ay = AY_TRES * (1 - v); ax = INCLINA * (1 - v);
    }else if(p < A_GIRO){
      fillP = 1;
    }else{
      const g = (p - A_GIRO) / (1 - A_GIRO);
      ay = -360 * spin(g);
      fillP = 1;
      arc = Math.sin(Math.PI * g);
      lift = -pieza.offsetHeight * .03 * arc;
      sc = 1 + .04 * arc;
    }

    sc *= 1 + .14 * cerca;
    lift += pieza.offsetHeight * .06 * cerca;            // baja un poco: se ve la boca de arriba
    sala.style.opacity = cerca ? cerca.toFixed(3) : "";
    sala.style.transform = x ? `translate3d(${x.toFixed(2)}px,0,0)` : "";
    const sx = sc * (1 + .018 * snapK), sy = sc * (1 - .015 * snapK);
    canvas.style.transform = lift || sx !== 1 || sy !== 1
      ? `translate3d(0,${lift.toFixed(2)}px,0) scale(${sx.toFixed(4)},${sy.toFixed(4)})` : "";

    /* color: el del recorrido, o el elegido con una transición corta */
    let col = colorDe(p);
    if(cambioColor){
      const k = clamp((performance.now() - cambioColor.t0) / 450);
      col = { rgb: mezcla(cambioColor.de, CUEROS[cambioColor.a], inOut(k)), nombre: cambioColor.a };
      if(k >= 1) cambioColor = null;
    }
    if(col.nombre !== cueroVisto){ cueroVisto = col.nombre; marcar(); }

    if(fig){
      const state = {
        ay: ay * deg, ax: (ax + (still ? 4 : 4 * (1 - p))) * deg,
        az: still ? 0 : clamp(lag * 60, -3, 3) * deg,
        level: VACIO + (LLENO - VACIO) * fillP,
        fill: fillP > .002 ? 1 : 0,
        open, tuft, tuftPos,
        glint: glintX, glintA,
        cuero: col.rgb, fur, furDark
      };
      const key = [state.ay, state.ax, state.az, state.level, open, tuft, ...tuftPos, glintX, glintA, ...col.rgb, canvas.width, canvas.height, fur.join()]
        .map(v => typeof v === "number" ? v.toFixed(5) : v).join("|");
      if(key !== drawn){ fig.draw(state); drawn = key; }
    }
    pieza.dataset.cuero = col.nombre;

    sombra.style.transform = `scale(${(1 - .1 * arc).toFixed(4)},${(1 - .12 * arc).toFixed(4)})`;
    sombra.style.opacity = (1 - .25 * arc).toFixed(3);

    const out = still ? 0 : outCubic(clamp((p - A_SALIDA) / (1 - A_SALIDA)));
    halo.style.transform = out ? `scale(${(1 + .24 * out).toFixed(4)})` : "";
    const haloBase = overlay && !still ? .3 + .7 * smooth(s) : 1;
    halo.style.opacity = out || haloBase < 1 ? (haloBase * (1 - .2 * out)).toFixed(3) : "";
    umbral.style.transform = `translate3d(${x.toFixed(2)}px,0,0) scaleX(${(still ? 1 : out).toFixed(4)})`;
    umbral.style.opacity = (still ? 1 : out).toFixed(3);

    const tramos = [[0, A_VACIO], [A_VACIO, A_CIERRE], [A_CIERRE, 1]];
    caps.forEach((c, i) => {
      if(still){ c.style.opacity = ""; c.style.transform = ""; return; }
      const [a0, a1] = tramos[i] || [0, 1], mid = (a0 + a1) / 2, f = .045;
      let o = Math.min(clamp((p - a0) / f + (i === 0 ? 1 : 0)), clamp((a1 - p) / f + (i === caps.length - 1 ? 1 : 0)));
      o *= smooth(s);
      c.style.opacity = o.toFixed(3);
      c.style.transform = `translate3d(0,${((1 - o) * (p < mid ? 10 : -10)).toFixed(2)}px,0)`;
    });
  }

  /* las muestras dicen qué color se ve, también cuando lo cambia el scroll */
  function marcar(){
    swatches.forEach(b => b.setAttribute("aria-pressed", String(b.dataset.cuero === cueroVisto)));
    if(nombre){
      const b = swatches.find(b => b.dataset.cuero === cueroVisto);
      nombre.textContent = b ? b.getAttribute("aria-label") || "" : "";
    }
  }
  swatches.forEach(b => b.addEventListener("click", () => {
    const de = colorDe(cur ? cur.p : 0).rgb;
    elegido = b.dataset.cuero;
    cambioColor = reduce.matches ? null : { de, a: elegido, t0: performance.now() };
    cueroVisto = elegido; marcar();
    drawn = null;
    schedule();
  }));
  document.addEventListener("pk:lang", () => { marcar(); });

  function tickSnap(t){
    if(!snapAt) return false;
    const e = t - snapAt;
    if(e >= 380){ snapAt = 0; snapK = 0; glintA = 0; return false; }
    snapK = e < 100 ? outCubic(e / 100) : 1 - (1 - Math.pow(1 - (e - 100) / 280, 4));
    const g = clamp(e / 350);
    glintX = -1.1 + 2.6 * outCubic(g);
    glintA = e < 350 ? .9 * Math.sin(Math.PI * g) : 0;
    return true;
  }

  function frame(t){
    const raw = last ? t - last : 16;
    const dt = Math.min(64, raw);
    last = t;
    if(fig) res.medir(raw);
    const tg = targets();
    if(!cur) cur = { p: tg.p, s: tg.s };
    const k = reduce.matches ? 1 : 1 - Math.exp(-dt / 70);
    const prev = cur.p;
    cur.p += (tg.p - cur.p) * k;
    cur.s += (tg.s - cur.s) * k;
    const done = Math.abs(tg.p - cur.p) < 1e-4 && Math.abs(tg.s - cur.s) < 1e-4;
    if(done){ cur.p = tg.p; cur.s = tg.s; }
    if(!reduce.matches && prev < A_CIERRE && cur.p >= A_CIERRE) snapAt = t;
    const snapping = tickSnap(t);
    const quiere = res.para(!done || snapping);
    if(fig && quiere !== scale){ scale = quiere; sizeCanvas(); }
    apply(cur.p, cur.s, done ? 0 : tg.p - cur.p);
    if(done && !snapping && !cambioColor){ raf = 0; last = 0; }
    else raf = requestAnimationFrame(frame);
  }
  function schedule(){ if(!raf) raf = requestAnimationFrame(frame); }

  function readFur(){
    const cs = getComputedStyle(document.documentElement);
    fur = hexColor(cs.getPropertyValue("--fur")) || fur;
    furDark = hexColor(cs.getPropertyValue("--fur-dark")) || furDark;
    drawn = null;
  }
  document.addEventListener("pk:fur", () => { readFur(); schedule(); });

  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", measure);
  if("ResizeObserver" in window) new ResizeObserver(measure).observe(cell);
  if(document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  reduce.addEventListener && reduce.addEventListener("change", measure);
  marcar();
  measure();
}

/* ------------------------------------------------------------
   El llavero en la galería de la compra: lleno, con la piel del color
   elegido en el configurador (data-cuero en <html>, evento pk:cuero) y
   el pelo del tono del previsualizador (--fur, pk:fur). Se gira
   arrastrando, sin tope, como la figura. Solo se dibuja si algo cambia.
   ------------------------------------------------------------ */
function initHuellaCompra(host){
  const pieza = host.querySelector(".pieza");
  const canvas = host.querySelector("canvas");
  const low = window.matchMedia("(max-width:700px), (pointer:coarse)").matches;
  let fig = crearHuellaGL(canvas, low);
  if(!fig){ host.hidden = true; return; }
  const galeria = host.closest(".galeria");
  const thumb = galeria && galeria.querySelector('[data-vista="huella"] canvas');
  const tx = thumb && thumb.getContext("2d");
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
  const dpr = window.devicePixelRatio || 1;
  const scale = Math.min(dpr, low ? 1.25 : 1.5);
  const deg = Math.PI / 180;
  const inOut = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const mezcla = (a, b, k) => a.map((v, i) => v + (b[i] - v) * k);
  const leerCuero = () => CUEROS[document.documentElement.dataset.cuero] || CUEROS.negro;
  const leerPelo = () => {
    const cs = getComputedStyle(document.documentElement);
    return [hexColor(cs.getPropertyValue("--fur")) || [.88, .65, .37], hexColor(cs.getPropertyValue("--fur-dark")) || [.69, .48, .2]];
  };
  let cuero = leerCuero(), [fur, furDark] = leerPelo();
  let desde = null, hasta = null, t0 = 0;
  let giro = 0, arrastre = null, drawn = "", raf = 0;

  function cambiar(){
    const c = leerCuero();
    if(c.join() === cuero.join() && !hasta) return;
    if(reduce.matches){ cuero = c; schedule(); return; }
    desde = cuero; hasta = c; t0 = performance.now();
    schedule();
  }
  function copiarThumb(){
    if(!tx || thumb.width < 2) return;
    const w = canvas.width, h = canvas.height, sy = .03, sh = h * (1 - sy), k = Math.min(thumb.width / w, thumb.height / sh);
    tx.clearRect(0, 0, thumb.width, thumb.height);
    tx.drawImage(canvas, 0, h * sy, w, sh, (thumb.width - w * k) / 2, (thumb.height - sh * k) / 2, w * k, sh * k);
  }
  function draw(now){
    raf = 0;
    if(!fig) return;
    let e = 1;
    if(hasta){
      e = Math.min(1, ((now || performance.now()) - t0) / 420);
      cuero = mezcla(desde, hasta, inOut(e));
      if(e >= 1) desde = hasta = null;
    }
    /* al cambiar de color se mece un poco y vuelve */
    const ay = -18 + giro + 12 * Math.sin(Math.PI * e);
    const key = [ay.toFixed(2), cuero.join(), fur.join(), canvas.width, canvas.height].join("|");
    if(key !== drawn){
      drawn = key;
      fig.draw({ ay: ay * deg, ax: 6 * deg, az: 0, level: -1.05, fill: 1, open: 0, tuft: 0, cuero, fur, furDark });
      copiarThumb();
    }
    if(hasta) schedule();
  }
  const schedule = () => { if(!raf) raf = requestAnimationFrame(draw); };
  function size(){
    const w = Math.max(1, Math.round(pieza.offsetWidth * scale)), h = Math.max(1, Math.round(pieza.offsetHeight * scale));
    if(w > 1 && (canvas.width !== w || canvas.height !== h)){ canvas.width = w; canvas.height = h; drawn = ""; }
    if(thumb){
      const tw = Math.max(1, Math.round(thumb.offsetWidth * dpr)), th = Math.max(1, Math.round(thumb.offsetHeight * dpr));
      if(thumb.width !== tw || thumb.height !== th){ thumb.width = tw; thumb.height = th; drawn = ""; }
    }
    schedule();
  }
  host.addEventListener("pointerdown", e => {
    if(e.button !== 0) return;
    arrastre = { x: e.clientX, g: giro };
    host.setPointerCapture(e.pointerId);
    host.classList.add("is-drag");
  });
  host.addEventListener("pointermove", e => {
    if(!arrastre) return;
    giro = arrastre.g + (e.clientX - arrastre.x) * 360 / Math.max(240, host.clientWidth);
    schedule();
  });
  const suelta = () => { arrastre = null; host.classList.remove("is-drag"); };
  host.addEventListener("pointerup", suelta);
  host.addEventListener("pointercancel", suelta);
  document.addEventListener("pk:cuero", cambiar);
  document.addEventListener("pk:fur", () => { [fur, furDark] = leerPelo(); schedule(); });
  window.addEventListener("resize", size);
  if("ResizeObserver" in window){ const ro = new ResizeObserver(size); ro.observe(pieza); if(thumb) ro.observe(thumb); }
  canvas.addEventListener("webglcontextlost", e => { e.preventDefault(); fig = null; });
  canvas.addEventListener("webglcontextrestored", () => { fig = crearHuellaGL(canvas, low); drawn = ""; schedule(); });
  size();
}

/* ------------------------------------------------------------
   El carrito: cada línea enseña el render de su pieza (la figura en su
   raza, el llavero en su color) con el tono de pelo con el que se
   configuró (pk-tono, lo guarda app.js al añadir al carrito). Un solo
   lienzo WebGL por tipo de pieza; cada dibujo se copia en 2D a la
   miniatura de su línea y después se sueltan los contextos.
   ------------------------------------------------------------ */
function initRendersCarrito(){
  const minis = [...document.querySelectorAll("canvas[data-render]:not([data-hecho])")];
  if(!minis.length) return;
  let fur = [.88, .65, .37], furDark = [.69, .48, .2], vacia = false;
  try{
    const g = JSON.parse(localStorage.getItem("pk-tono") || "null");
    if(g && g.vacia) vacia = true;
    else if(g){ fur = hexColor(g.c) || fur; furDark = hexColor(g.d) || furDark; }
  }catch(e){}
  const deg = Math.PI / 180, dpr = Math.min(window.devicePixelRatio || 1, 2);
  const motores = {};
  const motor = tipo => {
    if(!motores[tipo]){
      const c = document.createElement("canvas");
      motores[tipo] = { c, f: tipo === "huella" ? crearHuellaGL(c, false) : crearFiguraGL(c, false) };
    }
    return motores[tipo];
  };
  /* sin WebGL, la foto del producto */
  const foto = mini => {
    const src = mini.dataset.foto;
    if(src) mini.insertAdjacentHTML("afterend", `<img src="${src}" alt="" loading="lazy">`);
    mini.remove();
  };
  minis.forEach(mini => {
    mini.dataset.hecho = "1";
    const huella = mini.dataset.render === "huella", valor = mini.dataset.valor || "";
    const m = motor(huella ? "huella" : "figura");
    if(!m.f) return foto(mini);
    const w = Math.max(2, Math.round(mini.offsetWidth * dpr)), h = Math.max(2, Math.round(mini.offsetHeight * dpr));
    mini.width = w; mini.height = h;
    /* el lienzo es 4:5, como la vista de la compra; arriba se recorta un
       poco de aire (y en la figura, la cadena) y la pieza llena el cuadrado */
    const sy = huella ? .02 : .1, zoom = huella ? 1.02 : .96;
    const dh = h * zoom, dw = dh * .8 / (1 - sy);
    m.c.width = Math.round(dw); m.c.height = Math.round(dh / (1 - sy));
    if(huella){
      const cuero = /azul/i.test(valor) ? "azul" : /gris/i.test(valor) ? "gris" : /beige/i.test(valor) ? "beige" : "negro";
      m.f.draw({ ay: -18 * deg, ax: 6 * deg, az: 0, level: vacia ? .8 : -1.05, fill: vacia ? 0 : 1,
                 open: 0, tuft: 0, cuero: CUEROS[cuero], fur, furDark });
    }else{
      const raza = /pomer/i.test(valor) ? 1 : /golden/i.test(valor) ? 2 : 0, level = vacia ? -1.25 : 1.6;
      m.f.draw({ ay: -26 * deg, ax: 7 * deg, az: 0, level, phase: level * 6, fill: vacia ? 0 : 1, fur, furDark, raza });
    }
    const x = mini.getContext("2d");
    x.clearRect(0, 0, w, h);
    x.drawImage(m.c, 0, m.c.height * sy, m.c.width, m.c.height * (1 - sy), (w - dw) / 2, (h - dh) / 2 + h * (huella ? .03 : -.04), dw, dh);
  });
  Object.values(motores).forEach(m => {
    const ext = m.f && m.f.gl.getExtension("WEBGL_lose_context");
    if(ext) ext.loseContext();
  });
}